import { useEffect, useState, useRef, useMemo, useCallback, Fragment } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { THEMES } from '../themes';
import axios from 'axios';
import { Clock, Search, ChevronDown, ChevronLeft, ChevronRight, MapPin, Phone, Upload, CheckCircle, AlertCircle, Users, X, Trophy, Home, BookOpen, Instagram, Youtube, Facebook, Globe, ExternalLink, Zap, Share2, Download, Crown, CalendarPlus, CalendarDays, Copy, Megaphone, Navigation, Sunrise, Sun, Sunset, Moon } from 'lucide-react';
import { AMENITIES } from '../utils/amenities';
import { SportIcon } from '../components/SportIcon';
import MedalBadge from '../components/MedalBadge';
import { shareChampionsCard, downloadChampionsCard } from '../utils/championsCard';
import OpenPlayAwards, { cardAwards } from '../components/OpenPlayAwards';
import { fmtDate, fmtDateShort } from '../utils/dates';
import errText from '../../lib/errText';
import { BookingCalendar } from '../components/BookingCalendar';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// time string "HH:MM" → minutes
const t2m = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h === 0 && m === 0 ? 1440 : h * 60 + (m || 0);
};

// Theme presets + helpers live in ../themes (single source of truth,
// 2026-08-18) — the picker in ThemeAppearance derives from the same map.



// Sport icons now rendered via SportIcon SVG component

// Local-date YYYY-MM-DD (NOT UTC). toISOString() returns UTC which makes
// "today" wrong for users ahead of UTC (e.g. PH/UTC+8 sees yesterday before 8am).
const ymd = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => ymd();
// Date string (YYYY-MM-DD) for "now" in a given IANA timezone — so the booking
// window is anchored to the FACILITY's local day, not the visitor's browser.
const ymdTz = (tz, d = new Date()) => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch { return ymd(d); }
};
const addDays = (ymdStr, n) => {
  const [y, m, dd] = ymdStr.split('-').map(Number);
  const d = new Date(y, m - 1, dd); d.setDate(d.getDate() + n);
  return ymd(d);
};

const DOW_FULL = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDateChip(dateStr) {
  // "2026-06-28" → "Jun 28, 2026 · Sunday"
  const d = new Date(dateStr + 'T00:00:00');
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${DOW_FULL[d.getDay()]}`;
}

const TIMES = (() => {
  const t = [];
  // 5:00 AM → 11:30 PM
  for (let h = 5; h < 24; h++) for (let m = 0; m < 60; m += 30)
    t.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
  // 12:00 AM → 2:00 AM (+1) — for courts open past midnight
  for (let h = 24; h <= 26; h++) for (let m = 0; m < 60; m += 30) {
    if (h === 26 && m > 0) break; // cap at 2:00 AM
    t.push(`${h}:${String(m).padStart(2,'0')}`);
  }
  return t;
})();

function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (h >= 24) {
    const adjH = h - 24;
    if (adjH === 0) return `12:${String(m).padStart(2,'0')} AM`; // midnight = 24:00
    const ampm = adjH >= 12 ? 'PM' : 'AM';
    const h12 = adjH % 12 || 12;
    return `${h12}:${String(m).padStart(2,'0')} ${ampm} (+1)`; // e.g. 26:00 → 2:00 AM (+1)
  }
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
}

// Day-part bands for the time-chip grid (owner-approved mockup 2026-08-18).
// Customers were booking 7 AM meaning 7 PM — the two-letter suffix isn't
// enough. Bands make time-of-day SPATIAL: a labeled header row before the
// first chip of each part of the day. Purely visual — the chips stay one
// flat list, so start/end/cancel index logic is untouched.
const TIME_BANDS = [
  { label: 'Morning', Icon: Sunrise },
  { label: 'Afternoon', Icon: Sun },
  { label: 'Evening', Icon: Sunset },
  { label: 'Past midnight', Icon: Moon },
];
function timeBandIdx(t) {
  const h = Number(String(t).split(':')[0]);
  if (h >= 24) return 3; // the (+1) tail — physically the next calendar day
  if (h >= 17) return 2;
  if (h >= 12) return 1;
  return 0;
}

// Random per-browser-session key so the backend counts distinct PEOPLE (not
// page loads) for the urgency signals. Anonymous — never identifies anyone.
function sessionKey() {
  try {
    let k = sessionStorage.getItem('ab_sess_key');
    if (!k) {
      k = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('ab_sess_key', k);
    }
    return k;
  } catch { return null; }
}

// Rotating urgency line for the slot grid. Shows the MOST impressive TRUE
// signal (scarcity > booking recency > views > site traffic) and cycles
// through every qualifier with a soft fade. Numbers below their threshold are
// hidden entirely — "2 people viewed today" is anti-urgency. Never lies.
function UrgencyTicker({ urgency, T }) {
  const signals = useMemo(() => {
    if (!urgency) return [];
    const s = [];
    if (urgency.slots_left > 0 && urgency.slots_left <= 6) {
      s.push(`⚡ Only ${urgency.slots_left} slot${urgency.slots_left === 1 ? '' : 's'} left on this date`);
    }
    const lb = urgency.last_booking_minutes_ago;
    if (lb != null && lb <= 90) {
      s.push(lb < 2 ? '🔥 This court was booked just now' : lb < 60 ? `🔥 Booked ${lb} minutes ago` : '🔥 Booked an hour ago');
    }
    if (urgency.date_views_past_hour >= 4) {
      s.push(`👀 ${urgency.date_views_past_hour} people viewed this date in the past hour`);
    } else if (urgency.date_views_today >= 8) {
      s.push(`👀 ${urgency.date_views_today} people checked this date today`);
    }
    if (urgency.bookings_today >= 3) {
      s.push(`📈 ${urgency.bookings_today} bookings made today`);
    }
    if (urgency.site_visits_today >= 20) {
      s.push(`🚀 ${urgency.site_visits_today} people visited this site today`);
    }
    return s;
  }, [urgency]);
  const [idx, setIdx] = useState(0);
  const [show, setShow] = useState(true);
  useEffect(() => { setIdx(0); setShow(true); }, [signals.length]);
  useEffect(() => {
    if (signals.length < 2) return;
    const id = setInterval(() => {
      setShow(false);
      setTimeout(() => { setIdx(i => (i + 1) % signals.length); setShow(true); }, 300);
    }, 8000);
    return () => clearInterval(id);
  }, [signals.length]);
  if (!signals.length) return null;
  return (
    <p data-testid="sports-public-urgency"
      style={{ fontSize: 11, color: T.primary, fontWeight: 700, opacity: show ? 1 : 0, transition: 'opacity 0.3s' }}>
      {signals[Math.min(idx, signals.length - 1)]}
    </p>
  );
}

// Hero photo strip — gentle auto-drift that YIELDS to the visitor: swipe on
// touch, click-drag on desktop, and ‹ › arrows. Auto-drift pauses the moment
// they interact and resumes shortly after. A drag never opens the lightbox
// (only a clean click does), so they can flick through without triggering a photo.
function HeroCarousel({ items, T, onOpen }) {
  const trackRef = useRef(null);
  const st = useRef({ dragging: false, startX: 0, startScroll: 0, downScroll: 0, moved: false, paused: false, timer: null });
  const loop = items.length >= 3;
  const rendered = loop ? [...items, ...items] : items;

  // Auto-drift via rAF; seamless loop by rewinding one copy width.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || items.length < 2) return;
    let raf;
    const step = () => {
      const s = st.current;
      if (!s.paused && !s.dragging && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += 0.5;
        if (loop && el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
        else if (!loop && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [items.length, loop]);

  const pause = () => { const s = st.current; s.paused = true; if (s.timer) { clearTimeout(s.timer); s.timer = null; } };
  const resumeSoon = (ms = 1500) => { const s = st.current; if (s.timer) clearTimeout(s.timer); s.timer = setTimeout(() => { s.paused = false; }, ms); };

  const onDown = (e) => {
    const el = trackRef.current; const s = st.current;
    s.downScroll = el.scrollLeft; s.moved = false; pause();
    if (e.pointerType === 'touch') { s.dragging = false; return; } // let native touch scroll handle it
    s.dragging = true; s.startX = e.clientX; s.startScroll = el.scrollLeft;
    el.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const s = st.current; if (!s.dragging) return;
    trackRef.current.scrollLeft = s.startScroll - (e.clientX - s.startX);
  };
  const onUp = (e) => {
    const s = st.current; s.dragging = false;
    trackRef.current?.releasePointerCapture?.(e.pointerId);
    resumeSoon();
  };
  // Any real scroll motion between press and release marks it a drag, not a tap.
  const onScroll = () => { const s = st.current; if (Math.abs(trackRef.current.scrollLeft - s.downScroll) > 6) s.moved = true; };
  const nudge = (dir) => { const el = trackRef.current; if (!el) return; pause(); el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' }); resumeSoon(2200); };

  const arrowStyle = (side) => ({
    position: 'absolute', [side]: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 3,
    width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: T.card, border: `1px solid ${T.line}`, color: T.acc, cursor: 'pointer',
    boxShadow: `0 6px 20px ${T.glow}`,
  });

  return (
    <div style={{ position: 'relative', marginTop: 26 }}>
      <div ref={trackRef} data-testid="public-gallery-strip"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        onMouseEnter={pause} onMouseLeave={() => resumeSoon(300)}
        onTouchStart={pause} onTouchEnd={() => resumeSoon()} onScroll={onScroll}
        style={{ display: 'flex', gap: 10, padding: 10, overflowX: 'auto', overflowY: 'hidden',
          borderRadius: 14, border: `1px solid ${T.border}`, boxShadow: `0 20px 60px ${T.glow}`,
          scrollbarWidth: 'none', msOverflowStyle: 'none', cursor: 'grab', touchAction: 'pan-x', WebkitOverflowScrolling: 'touch' }}>
        <style>{`[data-testid="public-gallery-strip"]::-webkit-scrollbar{display:none}`}</style>
        {rendered.map((g, i) => (
          <div key={i} onClick={() => { if (!st.current.moved) onOpen(i % items.length); }}
            role="button" tabIndex={0} data-testid={`public-gallery-thumb-${i}`}
            style={{ position: 'relative', flexShrink: 0, cursor: 'zoom-in', borderRadius: 10, overflow: 'hidden' }}>
            <img src={g.thumb_url || g.url} alt={g.caption || ''} loading="lazy" draggable={false}
              style={{ height: 210, width: 'auto', minWidth: 160, maxWidth: 340, objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
            {g.caption && (
              <p style={{ position: 'absolute', left: 0, right: 0, bottom: 0, margin: 0, padding: '18px 12px 8px', fontSize: 11.5, color: '#fff', background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)' }}>
                {g.caption}
              </p>
            )}
          </div>
        ))}
      </div>
      {items.length >= 2 && (
        <>
          <button aria-label="Scroll left" data-testid="public-gallery-prev" onClick={() => nudge(-1)} style={arrowStyle('left')}>
            <ChevronLeft style={{ width: 20, height: 20 }} />
          </button>
          <button aria-label="Scroll right" data-testid="public-gallery-next" onClick={() => nudge(1)} style={arrowStyle('right')}>
            <ChevronRight style={{ width: 20, height: 20 }} />
          </button>
        </>
      )}
    </div>
  );
}

// Progressive image: show the (cached) thumbnail instantly, lightly blurred,
// then swap to the full-res once it finishes loading and clear the blur — so a
// tapped photo is never an empty placeholder.
function ProgressiveLightboxImg({ thumb, full, alt, style }) {
  const [src, setSrc] = useState(thumb || full);
  const [sharp, setSharp] = useState(!thumb || thumb === full);
  useEffect(() => {
    setSrc(thumb || full);
    setSharp(!thumb || thumb === full);
    if (thumb && thumb !== full) {
      const img = new Image();
      img.onload = () => { setSrc(full); setSharp(true); };
      img.src = full;
    }
  }, [thumb, full]);
  return (
    <img src={src} alt={alt}
      style={{ ...style, filter: sharp ? 'none' : 'blur(10px)', transition: 'filter 0.45s ease' }} />
  );
}

// Google Calendar template link for the booked slot. Hours ≥ 24 (overnight
// slots, e.g. "26:00" = 2 AM next day) roll over to the following date.
function googleCalUrl({ title, date, start, end, location }) {
  if (!date) return null;
  const part = (t, fallback) => {
    let [h, m] = (t || fallback).split(':').map(Number);
    let d = date;
    if (h >= 24) { h -= 24; d = addDays(date, 1); }
    return `${d.split('-').join('')}T${String(h).padStart(2, '0')}${String(m || 0).padStart(2, '0')}00`;
  };
  const params = new URLSearchParams({
    action: 'TEMPLATE', text: title || 'Court booking',
    dates: `${part(start, '09:00')}/${part(end || start, '10:00')}`,
    details: 'Show your QR check-in code when you arrive.',
    location: location || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function toMin(t) {
  if (t === '00:00') return 1440;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/** Short time mark for schedule grid row labels: 360→'6a', 390→':30', 1320→'10p' */
function fmtTimeMark(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m !== 0) return `:${String(m).padStart(2, '0')}`;
  if (h >= 24) { const a = h - 24; return `${a % 12 || 12}${a >= 12 ? 'p' : 'a'}`; }
  return `${h % 12 || 12}${h >= 12 ? 'p' : 'a'}`;
}

function isCourtOpenAt(court, timeMin) {
  return timeMin >= toMin(court.open_time) && timeMin < toMin(court.close_time);
}

// ── Day-band schedules (mirrors backend sports/booking_engine.py) ──
// A court may carry `schedules`: [{days:[0=Mon…6=Sun], open_time, close_time,
// rate_bands, min_duration_rules, …}]. Resolve the entry claiming the date's
// weekday and overlay it; courts without schedules are returned unchanged.
function effectiveCourt(court, dateStr) {
  const scheds = court?.schedules;
  if (!scheds?.length || !dateStr) return court;
  const wd = (new Date(dateStr + 'T12:00:00').getDay() + 6) % 7; // JS Sun=0 → backend Mon=0
  const s = scheds.find(x => (x.days || []).includes(wd));
  if (!s) return court;
  const eff = { ...court };
  ['open_time', 'close_time', 'rate_type', 'flat_rate', 'rate_bands', 'min_duration_rules', 'min_duration_minutes']
    .forEach(k => {
      const v = s[k];
      if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length)) eff[k] = v;
    });
  return eff;
}

// Minimum booking minutes for a booking STARTING at startMin (rule containing
// the start wins; falls back to the court-level minimum).
function minDurationAt(court, startMin) {
  for (const r of court?.min_duration_rules || []) {
    if (toMin(r.start) <= startMin && startMin < toMin(r.end)) return Number(r.min_minutes) || 0;
  }
  return Number(court?.min_duration_minutes) || 0;
}

// Hours chip label — date-aware when a date is known, honest when it isn't.
function hoursLabel(c, dateStr) {
  if (c?.schedules?.length && !dateStr) return 'Hours vary by day';
  const e = effectiveCourt(c, dateStr);
  return `${fmt12(e.open_time)}–${fmt12(e.close_time)}`;
}

const DOW_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Map play_now (absolute slot_index) to the position in the 0-based slots array
// by matching the slot whose end equals play_now.slot_end.
function pnIdx(sd) {
  if (!sd?.slots || !sd?.play_now) return -1;
  return sd.slots.findIndex(s => s.end === sd.play_now.slot_end);
}

// Time-point resolver for range selection. Slot buttons are TIME POINTS: tapping
// a button selects that time. A booking runs from the start point to the end
// point (exclusive of the end point's own slot). idx === slots.length means the
// closing time (end-of-day cap). Returns an 'HH:MM' string.
function pointTime(sd, idx) {
  const slots = sd?.slots || [];
  if (idx == null || !slots.length) return null;
  if (idx < slots.length) return slots[idx]?.start;
  return slots[slots.length - 1]?.end; // closing-time cap
}

// Compact public card for one Open Play session (shared by the home, booking
// and tournaments views of the tenant landing page).
// Fixture-style session card (UI checklist 1.1): status rail + glow by state,
// pulsing LIVE dot, avatar initials stack, honest counts, ONE state-routed CTA.
// State colors are constant across themes (gold live / green open); only the
// brand accent binds to the tenant theme.
const OP_CARD_STATES = {
  active: { color: '#FFC24B', label: () => 'Live', cta: 'Watch →', pulse: true },
  registration_open: { color: '#2EE07C', label: () => 'Registration open', cta: 'Register →', pulse: false },
  registration_closed: { color: '#F59E0B', label: () => 'Registration closed', cta: 'View →', pulse: false },
  ended: { color: '#8C8C86', label: () => 'Ended', cta: 'Results →', pulse: false },
};

function OpenPlayCard({ op, slug, T }) {
  const st = OP_CARD_STATES[op.status] || OP_CARD_STATES.registration_closed;
  const preview = op.player_preview || [];
  const overflow = Math.max(0, (op.player_count || 0) - preview.length);
  const fee = parseFloat(op.entry_fee || 0);
  const countLine = op.status === 'active'
    ? `${op.active_count ?? op.player_count ?? 0} playing now`
    : op.slots_left != null
      ? `${op.player_count || 0} in · ${op.slots_left} slot${op.slots_left === 1 ? '' : 's'} left`
      : `${op.player_count || 0} player${(op.player_count || 0) === 1 ? '' : 's'}`;
  return (
    <a data-testid={`public-open-play-${op.id}`}
      href={`/book/${slug}/open-play/${op.id}`}
      style={{ flex: '1 1 260px', display: 'flex', background: T.surface, borderRadius: 14, overflow: 'hidden',
        border: `1px solid ${op.status === 'active' ? st.color + '55' : op.status === 'registration_open' ? st.color + '45' : T.border}`,
        boxShadow: (op.status === 'active' || op.status === 'registration_open') ? `0 0 26px ${st.color}1C` : 'none',
        textDecoration: 'none', transition: 'border-color 0.15s' }}>
      <span style={{ width: 5, flexShrink: 0, background: `linear-gradient(180deg, ${st.color}, ${st.color}88)` }} />
      <div style={{ flex: 1, minWidth: 0, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, color: T.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{op.name}</p>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: st.color, background: st.color + '26', border: `1px solid ${st.color}66`, padding: '3px 10px', borderRadius: 999, flexShrink: 0 }}>
              {st.pulse && <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.color, boxShadow: `0 0 8px ${st.color}`, animation: 'agPulse 1.6s infinite' }} />}
              {st.label(op)}
            </span>
          </div>
          <p style={{ fontSize: 12, color: T.muted, margin: '5px 0 0' }}>
            {[fmtDate(op.date), `${op.game_mode === 'singles' ? 'Singles' : 'Doubles'} rotation`].filter(Boolean).join(' · ')}
            {' · '}<span style={{ color: fee > 0 ? st.color : T.muted, fontWeight: 600 }}>{fee > 0 ? `₱${op.entry_fee}` : 'Free'}</span>
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            {preview.length > 0 && (
              <span style={{ display: 'flex' }}>
                {preview.map((ini, i) => (
                  <span key={i} style={{ width: 22, height: 22, borderRadius: '50%', background: '#262626', border: `2px solid ${T.surface}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#C8C8C0', marginLeft: i === 0 ? 0 : -6 }}>{ini}</span>
                ))}
                {overflow > 0 && (
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: st.color + '26', border: `2px solid ${T.surface}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: st.color, marginLeft: -6 }}>+{overflow > 99 ? '99' : overflow}</span>
                )}
              </span>
            )}
            <span style={{ fontSize: 11, fontWeight: op.status === 'registration_open' ? 600 : 400, color: op.status === 'registration_open' ? st.color : T.muted }}>{countLine}</span>
          </div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: st.color, flexShrink: 0 }}>{st.cta}</span>
      </div>
    </a>
  );
}

// Compact, theme-aware winners strip for one division podium (public tournaments list).
function PodiumStrip({ p, T, onShare, onDownload }) {
  const rows = [
    { rank: 1, e: p.first },
    { rank: 2, e: p.second },
    { rank: 3, e: p.third },
  ].filter(r => r.e);
  return (
    <div data-testid={`podium-strip-${p.division || 'all'}`}
      style={{ background: 'rgba(255,213,74,0.05)', border: '1px solid rgba(255,213,74,0.2)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Crown style={{ width: 13, height: 13, color: '#FFD54A' }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: '#FFD54A', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Oswald, sans-serif' }}>
            {p.division ? `${p.division} — Champions` : 'Champions'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onShare} title="Share champions card" data-testid={`podium-strip-share-${p.division || 'all'}`}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,213,74,0.12)', border: '1px solid rgba(255,213,74,0.3)', borderRadius: 6, padding: '4px 10px', color: '#FFD54A', fontSize: 10, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase' }}>
            <Share2 style={{ width: 11, height: 11 }} /> Share
          </button>
          <button onClick={onDownload} title="Download champions card" data-testid={`podium-strip-download-${p.division || 'all'}`}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 10px', color: T.muted, fontSize: 10, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase' }}>
            <Download style={{ width: 11, height: 11 }} /> Card
          </button>
        </div>
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <MedalBadge rank={r.rank} size={20} />
            {(r.e.avatars || []).filter(Boolean).slice(0, 3).map((url, j) => (
              <img key={j} src={url} alt="" style={{ width: 18, height: 18, borderRadius: '50%',
                objectFit: 'cover', border: `1px solid ${T.border}`, flex: 'none' }} />
            ))}
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.e.team_name}</span>
            {(r.e.players || []).length > 1 && r.e.players.join(' · ') !== r.e.team_name && (
              <span style={{ fontSize: 11, color: T.muted, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.e.players.join(' · ')}</span>
            )}
          </div>
        ))}
      </div>
      {p.awards?.length > 0 && (
        <div style={{ color: T.text, borderTop: '1px solid rgba(255,213,74,0.15)', marginTop: 10, paddingTop: 2 }}>
          <OpenPlayAwards awards={p.awards} compact muted={T.muted} />
        </div>
      )}
    </div>
  );
}

export default function SportsPublicPage({ subdomainOverride = null }) {
  const { slug: slugParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [slug, setSlug] = useState(slugParam || null);
  const [subdomainLoading, setSubdomainLoading] = useState(!!subdomainOverride && !slugParam);
  const [facility, setFacility] = useState(null);
  const [theme, setTheme] = useState(THEMES['Velocity Red']);
  const [pageLoading, setPageLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Multi-page site navigation
  const [activePage, setActivePage] = useState(searchParams.get('page') || 'home');

  // Sport selector state — must choose sport first when facility has multiple sports
  const [selectedSport, setSelectedSport] = useState(null);
  // Booking flow
  // Browse Courts opens first — customers find per-court booking easier (owner, 2026-08-16)
  const [tab, setTab] = useState('courts');
  const [step, setStep] = useState('idle');

  // Smart booking
  const [date, setDate] = useState(today());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [smartResults, setSmartResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [calOpen, setCalOpen] = useState(true); // Smart booking calendar open/collapsed
  const [showTodaySlots, setShowTodaySlots] = useState(false);
  const [todaySlots, setTodaySlots] = useState(null);
  const [loadingTodaySlots, setLoadingTodaySlots] = useState(false);

  // Schedule tab
  const [scheduleDate, setScheduleDate] = useState(today());
  const [scheduleData, setScheduleData] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleCalOpen, setScheduleCalOpen] = useState(false);

  // Close schedule calendar on outside click
  useEffect(() => {
    if (!scheduleCalOpen) return;
    const handler = () => setScheduleCalOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [scheduleCalOpen]);

  // Per-court
  const [selectedCourtId, setSelectedCourtId] = useState('');
  const [courtDate, setCourtDate] = useState(today());
  const [slotData, setSlotData] = useState(null);
  const [slotError, setSlotError] = useState(false);
  const [slotLoading, setSlotLoading] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [playNowSel, setPlayNowSel] = useState(false); // Play Now (in-progress slot) selected
  const [pnAmount, setPnAmount] = useState(null);      // quoted price for the Play Now range
  const [rangeAmount, setRangeAmount] = useState(null); // quoted price for a normal slot range
  const [rangeError, setRangeError] = useState('');     // quote rejection (e.g. below minimum duration) shown at selection time
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [voucherErr, setVoucherErr] = useState('');
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [urgency, setUrgency] = useState(null);

  // Customer form
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '', address: '' });

  // Equipment rentals
  const [equipmentAvail, setEquipmentAvail] = useState(null);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [selectedRentals, setSelectedRentals] = useState({}); // {equipment_id: qty}

  // Hold
  const [holdData, setHoldData] = useState(null);
  const [countdown, setCountdown] = useState(300);
  const [gcashQrUrl, setGcashQrUrl] = useState(null);
  const [booking_holding, setBookingHolding] = useState(false);

  // Proof
  // How the booking reached the done screen: 'proof' (GCash proof submitted,
  // pending owner verification), 'reserved' (pay at court), 'confirmed'
  // (pay-on-arrival auto-confirm). Drives the done screen's status chip.
  const [doneKind, setDoneKind] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofKey, setProofKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const [error, setError] = useState('');
  const [alternatives, setAlternatives] = useState(null);

  // Resolve subdomain → slug when accessed via *.agri-books.com
  useEffect(() => {
    if (!subdomainOverride || slugParam) return;
    axios.get(`${BACKEND}/api/sports/subdomain-lookup?subdomain=${subdomainOverride}`)
      .then(r => setSlug(r.data.slug))
      .catch(() => setNotFound(true))
      .finally(() => setSubdomainLoading(false));
  }, [subdomainOverride, slugParam]);

  // Load facility
  useEffect(() => {
    if (!slug) return; // Wait for subdomain resolution
    axios.get(`${BACKEND}/api/sports/public/${slug}`, { params: { sk: sessionKey() } })
      .then(r => {
        // The permanent /book/{slug} link (printed QRs) forwards to the
        // facility's memorable web address when one is claimed — customers
        // remember pfarm.agri-books.com, not the path form (owner 2026-08-09).
        // Apex production hosts only: dev/preview hosts can't do wildcards.
        const sub = r.data.custom_subdomain;
        const host = window.location.hostname;
        if (sub && (host === 'agri-books.com' || host === 'www.agri-books.com')) {
          window.location.replace(`https://${sub}.agri-books.com/${window.location.search}`);
          return; // keep the splash-style pageLoading until the redirect lands
        }
        setFacility(r.data);
        setTheme(THEMES[r.data.theme_preset] || THEMES['Velocity Red']);
        setPageLoading(false);
      })
      .catch(() => { setNotFound(true); setPageLoading(false); });
  }, [slug]);

  // Site builder config
  const siteBuilder = facility?.site_builder || {};
  // 2026-07: every facility gets the full mini-site — Home | Book | Events.
  // (site_builder.home_enabled / tournaments_page_enabled are legacy opt-ins,
  // now always on; kept in settings for backward compat only.)
  const hasMultiPage = true;

  // Sectioned photos: each Site Builder section renders in its own spot.
  // Facility photos play in the hero strip (all photos stand in when nothing
  // is tagged yet, hero image when the gallery is empty).
  const galleryItems = (siteBuilder.gallery || []).filter(g => g.url);
  const photosBySection = (sid) => galleryItems.filter(g => (g.section || 'facility') === sid);
  const facilityPhotos = photosBySection('facility');
  const courtsPhotos = photosBySection('courts');
  const amenitiesPhotos = photosBySection('amenities');
  const eventsPhotos = photosBySection('events');
  const heroStrip = facilityPhotos.length > 0 ? facilityPhotos
    : galleryItems.length > 0 ? galleryItems
    : (facility?.hero_url ? [{ url: facility.hero_url, thumb_url: facility.hero_url, caption: '' }] : []);
  // Lightbox is scoped to one section's photo list: { list, idx } or null
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => {
    if (!lightbox) return;
    const n = lightbox.list.length;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox(lb => lb && { ...lb, idx: (lb.idx + 1) % n });
      if (e.key === 'ArrowLeft') setLightbox(lb => lb && { ...lb, idx: (lb.idx - 1 + n) % n });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox === null]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigatePage = (page) => {
    setActivePage(page);
    setSearchParams(page === 'home' ? {} : { page });
    window.scrollTo({ top: 0 });
  };

  // Tournaments for this facility (public banner)
  const [pubTournaments, setPubTournaments] = useState([]);
  const [pubOpenPlay, setPubOpenPlayRaw] = useState([]);
  const [pubOpenPlayEnded, setPubOpenPlayEnded] = useState([]);
  useEffect(() => {
    if (!slug) return;
    axios.get(`${BACKEND}/api/sports/public/${slug}/tournaments`)
      .then(r => setPubTournaments(r.data.tournaments || []))
      .catch(() => {});
    axios.get(`${BACKEND}/api/sports/public/${slug}/open-play`)
      .then(r => {
        // Split into "current" (registration/live) vs "recently ended" (event history).
        // Landing page shows current; tournaments page shows both.
        const raw = (Array.isArray(r.data) ? r.data : []).filter(s => !s.archived);
        setPubOpenPlayRaw(raw.filter(s => s.status !== 'ended'));
        setPubOpenPlayEnded(raw.filter(s => s.status === 'ended'));
      })
      .catch(() => {});
  }, [slug]);

  // "Play today" chips — next bookable slot per court (landing redesign)
  const [playToday, setPlayToday] = useState([]);
  useEffect(() => {
    if (!slug) return;
    axios.get(`${BACKEND}/api/sports/public/${slug}/play-today`)
      .then(r => setPlayToday(r.data.options || []))
      .catch(() => {});
  }, [slug]);

  // Jump straight from a Play-today chip into the per-court booking grid
  const jumpToSlot = (o) => {
    if (uniqueSports.length > 1 && o.sport) setSelectedSport(o.sport);
    setTab('courts');
    setSelectedCourtId(o.court_id);
    setCourtDate(o.date);
    setStep('idle');
    navigatePage('booking');
    setTimeout(() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }), 120);
  };

  // Sorted tournament views: live → registration open → upcoming → completed (recent first)
  const TN_RANK = { in_progress: 0, registration_open: 1, registration_closed: 2, completed: 3 };
  const sortedTournaments = [...pubTournaments].sort((a, b) =>
    ((TN_RANK[a.status] ?? 9) - (TN_RANK[b.status] ?? 9)) ||
    ((Date.parse(b.created_at || b.date_start || '') || 0) - (Date.parse(a.created_at || a.date_start || '') || 0)));
  const activeTournaments = sortedTournaments.filter(t => t.status === 'in_progress' || t.status === 'registration_open');

  // Per-tenant browser tab title (humans + JS-running crawlers like Google)
  useEffect(() => {
    const nm = facility?.facility?.name;
    if (nm) document.title = `${nm} — Book Courts & Join Tournaments`;
    return () => { document.title = 'AgriBooks | Smart Business Management System'; };
  }, [facility]);

  useEffect(() => {
    if (step !== 'timer' && step !== 'proof') return;
    if (countdown <= 0) { setStep('expired'); return; }
    const t = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [step, countdown]);

  // Fetch GCash QR URL when hold is received
  useEffect(() => {
    if (!holdData?.gcash?.qr_key) return;
    axios.get(`${BACKEND}/api/sports/public/${slug}/asset-url`, { params: { key: holdData.gcash.qr_key } })
      .then(r => setGcashQrUrl(r.data.url))
      .catch(() => {});
  }, [holdData, slug]);

  // Load availability for the per-court tab. `silent` skips the loading spinner,
  // the view-log, and error-wipe — used by the background refreshers below so
  // they can repaint cells without disturbing what the user is looking at.
  const loadCourtSlots = useCallback(({ silent = false } = {}) => {
    if (!selectedCourtId || !courtDate) return;
    if (!silent) setSlotLoading(true);
    axios.get(`${BACKEND}/api/sports/public/${slug}/availability`,
      { params: { court_id: selectedCourtId, date: courtDate, sk: sessionKey(), ...(silent ? { log_view: false } : {}) } })
      .then(r => { setSlotData(r.data); setSlotError(false); setUrgency(r.data.urgency || null); })
      .catch(() => { if (!silent) { setSlotData(null); setSlotError(true); } })
      .finally(() => { if (!silent) setSlotLoading(false); });
  }, [selectedCourtId, courtDate, slug]);

  // Court/date changed → clear any in-progress pick and (re)load availability.
  useEffect(() => {
    if (!selectedCourtId || !courtDate) return;
    setRangeStart(null); setRangeEnd(null);
    loadCourtSlots();
  }, [loadCourtSlots]); // eslint-disable-line react-hooks/exhaustive-deps

  // Schedule-cell → Browse Courts handoff: once this court's grid is loaded,
  // pre-select the tapped slot as the START time (end stays the customer's
  // choice). One-shot — cleared whether or not the slot is still available.
  const [pendingSlotStart, setPendingSlotStart] = useState(null);
  useEffect(() => {
    if (!pendingSlotStart || !slotData?.slots) return;
    const idx = slotData.slots.findIndex(s => s.start === pendingSlotStart && s.available);
    if (idx >= 0) { setRangeStart(idx); setRangeEnd(null); }
    setPendingSlotStart(null);
  }, [pendingSlotStart, slotData]);

  // Self-update: the moment a booking completes, silently refresh the grid so
  // returning via "Book Another Slot" shows the just-taken slots crossed out.
  useEffect(() => {
    if (step === 'done') loadCourtSlots({ silent: true });
  }, [step, loadCourtSlots]);

  // Quiet live refresh: pick up OTHER people's bookings without a manual reload
  // — but ONLY while idly browsing the grid. It suspends the instant a slot is
  // selected, a form/timer/receipt is open, or the tab is hidden, so it can
  // never yank the view out from under a booking in progress.
  useEffect(() => {
    if (tab !== 'courts' || !selectedCourtId || !courtDate) return;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (step !== 'idle' && step !== 'results') return;
      if (rangeStart !== null || rangeEnd !== null || playNowSel) return;
      loadCourtSlots({ silent: true });
    }, 15000);
    return () => clearInterval(id);
  }, [tab, selectedCourtId, courtDate, step, rangeStart, rangeEnd, playNowSel, loadCourtSlots]);

  // Quote the Play Now range (pro-rated start slot + any full slots the user extended into)
  useEffect(() => {
    if (!playNowSel || !slotData?.play_now || !selectedCourtId) { setPnAmount(null); return; }
    const si = pnIdx(slotData);
    const endIdx = rangeEnd != null ? rangeEnd : si + 1;
    const endTime = pointTime(slotData, endIdx) || slotData.play_now.slot_end;
    axios.get(`${BACKEND}/api/sports/public/${slug}/quote`, {
      params: { court_id: selectedCourtId, date: courtDate, start: slotData.play_now.start, end: endTime, play_now: true }
    }).then(r => setPnAmount(r.data?.amount ?? null)).catch(() => setPnAmount(slotData.play_now.price ?? null));
  }, [playNowSel, rangeEnd, slotData, selectedCourtId, courtDate, slug]);

  // Quote a normal (non-Play-Now) slot range so customers see the price BEFORE booking.
  // A rejection (e.g. below the minimum duration for this time band) is surfaced
  // HERE, at selection time — not after the customer has filled in the form.
  useEffect(() => {
    if (playNowSel || rangeStart === null || rangeEnd === null || !slotData?.slots || !selectedCourtId) {
      setRangeAmount(null); setRangeError(''); return;
    }
    const start = slotData.slots[rangeStart]?.start;
    const end = pointTime(slotData, rangeEnd);
    if (!start || !end) { setRangeAmount(null); setRangeError(''); return; }
    axios.get(`${BACKEND}/api/sports/public/${slug}/quote`, {
      params: { court_id: selectedCourtId, date: courtDate, start, end }
    }).then(r => { setRangeAmount(r.data?.amount ?? null); setRangeError(''); })
      .catch(e => {
        setRangeAmount(null);
        const d = e.response?.data?.detail;
        setRangeError(typeof d === 'string' ? d : (d?.message || 'This time range can\'t be booked'));
      });
  }, [playNowSel, rangeStart, rangeEnd, slotData, selectedCourtId, courtDate, slug]);

  const T = theme;
  const courts = facility?.courts || [];
  const uniqueSports = [...new Set(courts.map(c => c.sport))];
  const bookingNotice = facility?.booking_notice || null;

  // Compute max bookable date based on facility's advance booking limit
  const facilityTz = facility?.timezone || 'Asia/Manila';
  const todayLocal = ymdTz(facilityTz);
  const maxDate = facility?.max_advance_days ? addDays(todayLocal, Number(facility.max_advance_days)) : null;

  // Current minute-of-day in the facility's timezone. Recomputed each render so
  // buttons re-disable as the clock crosses each slot's start time. A 30-second
  // ticker (below) keeps this fresh even when nothing else is causing a rerender.
  const [__pastSlotTick, __setPastSlotTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => __setPastSlotTick(v => v + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const facilityNowMin = (() => {
    void __pastSlotTick; // keep the recompute tied to the ticker
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: facilityTz, hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(new Date());
      const g = (k) => parseInt(parts.find(p => p.type === k)?.value || '0', 10);
      return (g('hour') % 24) * 60 + g('minute');
    } catch {
      const d = new Date(); return d.getHours() * 60 + d.getMinutes();
    }
  })();
  const isTodayFacility = (dateStr) => dateStr === todayLocal;
  // A slot is "past" only once its END has already passed in the facility's
  // timezone. We deliberately keep the currently-in-progress slot visible
  // (start <= now < end) so the Play Now pro-rated button can still use it
  // as its base — e.g. at 8:05 PM the 8:00 PM slot must remain visible.
  const slotIsPast = (slot, dateStr) => isTodayFacility(dateStr) && toMin(slot.end) <= facilityNowMin;

  // Trim free intervals already in the past (facility clock) — only meaningful
  // when the shown date IS facility-today. Spanning-now intervals advance their
  // start to the next 30-min boundary.
  const trimPastIntervals = (ivs, dateStr) => {
    if (!isTodayFacility(dateStr)) return ivs || [];
    return (ivs || []).map(iv => {
      if (toMin(iv.end) <= facilityNowMin) return null;
      if (toMin(iv.start) <= facilityNowMin) {
        const nb = Math.ceil((facilityNowMin + 1) / 30) * 30;
        if (nb >= toMin(iv.end)) return null;
        return { ...iv, start: `${String(Math.floor(nb / 60)).padStart(2, '0')}:${String(nb % 60).padStart(2, '0')}` };
      }
      return iv;
    }).filter(Boolean);
  };

  // Auto-select sport if facility only has one sport type
  useEffect(() => {
    if (facility && uniqueSports.length === 1 && !selectedSport) {
      setSelectedSport(uniqueSports[0]);
    }
  }, [facility]);

  // Courts filtered by selected sport
  const filteredCourts = selectedSport ? courts.filter(c => c.sport === selectedSport) : courts;

  // ── Free time at a glance (owner 2026-08-17) ─────────────────
  // Court cards show today's open intervals; inside a court, a sibling strip
  // shows the other courts' open intervals for the date being viewed. Same
  // availability endpoint as the Schedule tab; log_view=false so glance
  // renders never inflate the urgency counters.
  const [cardsToday, setCardsToday] = useState(null);          // {courtId: {open, free_intervals}}
  const [otherCourtsDay, setOtherCourtsDay] = useState(null);  // {date, courts: [{courtId, name, open, free_intervals}]}

  useEffect(() => {
    if (tab !== 'courts' || selectedCourtId || !courts.length || !slug || cardsToday) return;
    Promise.all(courts.map(c =>
      axios.get(`${BACKEND}/api/sports/public/${slug}/availability`,
        { params: { court_id: c.id, date: todayLocal, log_view: false } })
        .then(r => ({ courtId: c.id, open: r.data.open, free_intervals: r.data.free_intervals || [] }))
        .catch(() => null)
    )).then(rs => {
      const m = {};
      rs.filter(Boolean).forEach(r => { m[r.courtId] = r; });
      setCardsToday(m);
    });
  }, [tab, selectedCourtId, courts, slug, todayLocal, cardsToday]);

  // NOTE: depend on courts + selectedSport (stable), never filteredCourts —
  // it's a fresh array every render and would loop this effect.
  useEffect(() => {
    if (tab !== 'courts' || !selectedCourtId || !courtDate || !slug) { setOtherCourtsDay(null); return; }
    const pool = selectedSport ? courts.filter(c => c.sport === selectedSport) : courts;
    const others = pool.filter(c => c.id !== selectedCourtId);
    if (!others.length) { setOtherCourtsDay(null); return; }
    let cancelled = false;
    Promise.all(others.map(c =>
      axios.get(`${BACKEND}/api/sports/public/${slug}/availability`,
        { params: { court_id: c.id, date: courtDate, log_view: false } })
        .then(r => ({ courtId: c.id, name: c.name, open: r.data.open, free_intervals: r.data.free_intervals || [] }))
        .catch(() => null)
    )).then(rs => {
      if (cancelled) return;
      setOtherCourtsDay({ date: courtDate, courts: rs.filter(Boolean) });
    });
    return () => { cancelled = true; };
  }, [tab, selectedCourtId, courtDate, slug, courts, selectedSport]);

  // ─── Schedule tab: load availability for all courts ──────────
  useEffect(() => {
    if (tab !== 'schedule' || !filteredCourts.length || !slug) return;
    setScheduleLoading(true);
    Promise.all(
      filteredCourts.map(c =>
        axios.get(`${BACKEND}/api/sports/public/${slug}/availability`, {
          params: { court_id: c.id, date: scheduleDate, log_view: false }
        }).then(r => ({ courtId: c.id, ...r.data }))
          .catch(() => ({ courtId: c.id, open: false, slots: [] }))
      )
    ).then(results => {
      const d = {};
      results.forEach(r => { d[r.courtId] = r; });
      setScheduleData(d);
    }).finally(() => setScheduleLoading(false));
  }, [tab, scheduleDate, slug, filteredCourts.length]);

  const changeScheduleDate = (delta) => {
    const d = new Date(scheduleDate + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    const next = ymd(d);
    // Clamp to the bookable window (facility-local): never before today, never past maxDate.
    if (next < todayLocal) return;
    if (maxDate && next > maxDate) return;
    setScheduleDate(next);
  };

  // Build schedule grid: time marks + cell types (booked/available/closed)
  const { scheduleTimeMarks, scheduleGrid } = useMemo(() => {
    if (!filteredCourts.length) return { scheduleTimeMarks: [], scheduleGrid: {} };
    // Day-band schedules: use each court's hours for the date being viewed.
    const effCourts = filteredCourts.map(c => effectiveCourt(c, scheduleDate));
    const openMins  = effCourts.map(c => toMin(c.open_time));
    const closeMins = effCourts.map(c => toMin(c.close_time));
    const startMin  = Math.min(...openMins);
    const endMin    = Math.max(...closeMins);
    const marks = [];
    for (let t = startMin; t <= endMin; t += 30) marks.push(t);

    const g = {};
    const skip = new Set();
    effCourts.forEach(court => {
      const slots = (scheduleData[court.id]?.slots) || [];
      marks.forEach(timeMin => {
        const key = `${court.id}|${timeMin}`;
        if (skip.has(key)) { g[key] = { type: 'skip' }; return; }
        const slot = slots.find(s => toMin(s.start) === timeMin);
        if (!slot) {
          g[key] = { type: isCourtOpenAt(court, timeMin) ? 'between' : 'closed' };
          return;
        }
        const dur = toMin(slot.end) - toMin(slot.start);
        const rowSpan = Math.max(1, Math.round(dur / 30));
        for (let r = 1; r < rowSpan; r++) skip.add(`${court.id}|${timeMin + r * 30}`);
        g[key] = slot.available ? { type: 'available', slot, rowSpan } : { type: 'booked', rowSpan };
      });
    });
    return { scheduleTimeMarks: marks, scheduleGrid: g };
  }, [filteredCourts, scheduleData, scheduleDate]);

  // ─── Smart Search ────────────────────────────────────────────
  const handleSmartSearch = async () => {
    if (!startTime || !endTime) { setError('Please select a start and end time'); return; }
    if (toMin(endTime) <= toMin(startTime)) { setError('End time must be after start time'); return; }
    // Minimum duration is NOT hard-blocked here: the backend relaxes it when
    // the free window is smaller than the minimum (squeeze rule), so short
    // requests may still be legitimately bookable in gaps / near closing.
    setSearching(true); setError(''); setSmartResults(null); setShowTodaySlots(false);
    try {
      const res = await axios.post(`${BACKEND}/api/sports/public/${slug}/smart`, {
        date, start_time: startTime, end_time: endTime, sport: selectedSport || sportFilter || undefined,
      });
      setSmartResults(res.data);
    } catch (e) {
      setError(errText(e, 'Search failed. Please try again.'));
    } finally { setSearching(false); }
  };

  // ─── Smart time filtering ────────────────────────────────────
  // minStartMin: if today (in facility tz) is selected, earliest start is next
  // 30-min boundary from the facility's current time. Previously used browser
  // local time, which was wrong for visitors in a different timezone.
  const minStartMin = (() => {
    if (date !== todayLocal) return 0;
    return Math.ceil((facilityNowMin + 1) / 30) * 30; // +1 so "right now" slot is excluded
  })();

  // Bound the smart-search time pickers to the facility's REAL operating hours
  // (respect each court's open_time/close_time) instead of a fixed 5am–2am range.
  // Uses the sport-matched courts so the window matches what the customer filtered.
  const smartCourts = (() => {
    const sp = selectedSport || sportFilter;
    const pool = sp ? courts.filter(c => c.sport === sp) : courts;
    return pool.length ? pool : courts;
  })();
  // Day-band schedules: bound the pickers to the hours of the SELECTED date.
  const smartEffCourts = smartCourts.map(c => effectiveCourt(c, date));
  const facilityOpenMin = smartEffCourts.length ? Math.min(...smartEffCourts.map(c => toMin(c.open_time))) : toMin('05:00');
  const facilityCloseMin = smartEffCourts.length ? Math.max(...smartEffCourts.map(c => toMin(c.close_time))) : toMin('24:00');

  // Per-time-band minimum duration: the friendliest rule across courts at the
  // chosen start time (a court requiring more simply won't be offered).
  const smartMinNeeded = (startTime && smartEffCourts.length)
    ? Math.min(...smartEffCourts.map(c => minDurationAt(c, toMin(startTime))))
    : 0;

  // Filtered options — start must be within [open, close), end within (start, close].
  // End options also respect the minimum duration at the chosen start time.
  const validStartTimes = TIMES.filter(t => toMin(t) >= Math.max(minStartMin, facilityOpenMin) && toMin(t) < facilityCloseMin);
  const endFloorMin = startTime ? toMin(startTime) + Math.max(smartMinNeeded, 30) : facilityOpenMin;
  const validEndTimes   = TIMES.filter(t => toMin(t) >= endFloorMin && toMin(t) <= facilityCloseMin);

  // Auto-reset startTime when date changes to today and chosen time is now in the past
  useEffect(() => {
    if (startTime && date === today() && toMin(startTime) < minStartMin) {
      setStartTime('');
      setEndTime('');
      setError('');
    }
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-reset endTime when startTime changes and endTime is no longer valid.
  // NOTE: only clear on a truly impossible order (end <= start) — an end below
  // the band minimum can still be legitimate via the squeeze rule (a gap or
  // end-of-day window shorter than the minimum), so leave it for the backend.
  useEffect(() => {
    if (endTime && startTime && toMin(endTime) <= toMin(startTime)) {
      setEndTime('');
      setError('');
    }
  }, [startTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear a stale start/end if the facility window changes (e.g. sport filter)
  // so a time outside the real open/close hours can never remain selected.
  useEffect(() => {
    if (startTime && (toMin(startTime) < facilityOpenMin || toMin(startTime) >= facilityCloseMin)) {
      setStartTime(''); setEndTime('');
    } else if (endTime && toMin(endTime) > facilityCloseMin) {
      setEndTime('');
    }
  }, [facilityOpenMin, facilityCloseMin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Today's Slots quick view ────────────────────────────────
  const handleViewTodaySlots = async () => {
    if (showTodaySlots) { setShowTodaySlots(false); return; }
    setLoadingTodaySlots(true); setShowTodaySlots(true); setSmartResults(null);
    const todayDate = today();
    const courtsToCheck = filteredCourts.length > 0 ? filteredCourts : courts;
    try {
      const results = await Promise.all(
        courtsToCheck.map(c =>
          axios.get(`${BACKEND}/api/sports/public/${slug}/availability`, {
            params: { court_id: c.id, date: todayDate, log_view: false }
          }).then(r => ({ courtId: c.id, name: c.name, ...r.data }))
            .catch(() => ({ courtId: c.id, name: c.name, free_intervals: [], open: false }))
        )
      );
      const slotMap = {};
      results.forEach(r => { slotMap[r.courtId] = r; });
      setTodaySlots(slotMap);
    } finally { setLoadingTodaySlots(false); }
  };

  // ─── Load Equipment Availability ─────────────────────────────
  const loadEquipment = (courtId, dateStr, start, end) => {
    const court = courts.find(c => c.id === courtId);
    if (!court) return;
    const sport = court.sport;
    setEquipmentLoading(true); setEquipmentAvail(null); setSelectedRentals({});
    axios.get(`${BACKEND}/api/sports/public/${slug}/equipment-availability`, {
      params: { sport, date: dateStr, start_time: start, end_time: end }
    })
      .then(r => setEquipmentAvail(r.data?.equipment || []))
      .catch(() => setEquipmentAvail([]))
      .finally(() => setEquipmentLoading(false));
  };

  // ─── Hold ────────────────────────────────────────────────────
  const initiateHold = async (courtId, dateStr, start, end, isPlayNow = false) => {
    setBookingHolding(true); setError('');
    if (!customer.name.trim() || !customer.phone.trim()) {
      setError('Please enter your name and phone number.'); setBookingHolding(false); return;
    }
    try {
      const res = await axios.post(`${BACKEND}/api/sports/public/${slug}/hold`, {
        court_id: courtId, date: dateStr, start_time: start, end_time: end, customer,
        play_now: isPlayNow,
        voucher_code: voucherInfo ? voucherInfo.code : '',
      });
      // Keep the slot context alongside the hold response so the done screen
      // can render a full receipt without depending on selection state.
      setHoldData({
        ...res.data,
        court_name: courts.find(c => c.id === courtId)?.name || '',
        date: dateStr, start_time: start, end_time: end,
      });

      // Submit rentals if any were selected
      const rentalItems = Object.entries(selectedRentals)
        .filter(([, qty]) => qty > 0)
        .map(([eqId, qty]) => {
          const eq = (equipmentAvail || []).find(e => e.equipment_id === eqId);
          // Find the best available window for this item
          const window = (eq?.windows || []).find(w => w.available_qty >= qty);
          return {
            equipment_id: eqId,
            name: eq?.name || eqId,
            qty,
            unit: eq?.unit || 'piece',
            start_time: window?.start || start,
            end_time: window?.end || end,
          };
        });

      if (rentalItems.length > 0) {
        try {
          await axios.post(`${BACKEND}/api/sports/public/${slug}/submit-rental`, {
            booking_id: res.data.booking_id,
            items: rentalItems,
          });
        } catch (rentalErr) {
          // Non-fatal: booking succeeded, rental failed — show warning
          console.warn('Rental submission failed:', rentalErr);
        }
      }

      if (res.data.confirmed) {
        // Pay on Arrival — booking already confirmed, no GCash needed
        setDoneKind('confirmed');
        setStep('done');
      } else {
        // Normal GCash flow
        setCountdown(res.data.hold_seconds || 300);
        setStep('timer');
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === 'slot_taken') {
        setAlternatives(d.alternatives);
        setError('That slot was just taken. See alternatives below.');
        setStep('idle');
      } else {
        setError(typeof d === 'string' ? d : 'Failed to reserve slot. Try again.');
      }
    } finally { setBookingHolding(false); }
  };

  // ─── Proof upload ─────────────────────────────────────────────
  const handleProofUpload = async () => {
    if (!proofFile || !holdData) return;
    setUploading(true); setError('');
    try {
      // Upload via server proxy — bypasses R2 CORS restrictions
      const formData = new FormData();
      formData.append('file', proofFile);
      formData.append('booking_id', holdData.booking_id);
      const res = await axios.post(
        `${BACKEND}/api/sports/public/${slug}/upload-proof-file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setProofKey(res.data.proof_key);
      toast_style('Proof uploaded! Submitting booking…');
      await handleSubmitProof(res.data.proof_key);
    } catch (e) {
      setError('Upload failed. Check your connection and try again.');
    } finally { setUploading(false); }
  };

  const handleSubmitProof = async (key) => {
    setSubmitting(true); setError('');
    try {
      await axios.post(`${BACKEND}/api/sports/public/${slug}/submit-proof`, {
        booking_id: holdData.booking_id, proof_key: key || proofKey,
      });
      setDoneKind('proof');
      setStep('done');
    } catch (e) {
      const status = e.response?.status;
      if (status === 410) { setStep('expired'); }
      else { setError(errText(e, 'Submission failed. Try again.')); }
    } finally { setSubmitting(false); }
  };

  const [payingOnline, setPayingOnline] = useState(false);
  const payOnline = async (amountChoice) => {
    if (!holdData) return;
    setPayingOnline(true); setError('');
    try {
      const r = await axios.post(`${BACKEND}/api/sports/public/${slug}/pay/checkout`, {
        booking_id: holdData.booking_id, amount_choice: amountChoice,
      });
      if (r.data?.checkout_url) { window.location.href = r.data.checkout_url; }
      else setError('Could not start payment.');
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'object' ? d.message : (d || 'Could not start payment.'));
      setPayingOnline(false);
    }
  };

  const [reserving, setReserving] = useState(false);
  const payAtCourt = async () => {
    if (!holdData) return;
    setReserving(true); setError('');
    try {
      await axios.post(`${BACKEND}/api/sports/public/${slug}/pay/reserve`, { booking_id: holdData.booking_id });
      setDoneKind('reserved');
      setStep('done');
    } catch (e) {
      const status = e.response?.status;
      if (status === 410) { setStep('expired'); }
      else { setError(errText(e, 'Could not reserve. Try again.')); }
    } finally { setReserving(false); }
  };


  // Simple toast style message without full toast lib
  const [toastMsg, setToastMsg] = useState('');
  const toast_style = (msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(''), 3000); };

  const resetFlow = () => {
    setStep('idle'); setHoldData(null); setProofFile(null); setProofKey(null); setDoneKind(null);
    setCountdown(300); setGcashQrUrl(null); setError(''); setAlternatives(null);
    setSelectedOption(null); setSmartResults(null);
    setRangeStart(null); setRangeEnd(null); setPlayNowSel(false); setPnAmount(null);
    setVoucherCode(''); setVoucherInfo(null); setVoucherErr('');
    setEquipmentAvail(null); setSelectedRentals({});
  };

  const calcAmount = () => {
    if (selectedOption) return selectedOption.amount;
    if (rangeStart !== null && rangeEnd !== null && slotData?.slots) {
      const sm = slotData.court?.slot_minutes || 30;
      const s = rangeStart; const e = rangeEnd;
      // Get price by calling backend on hold, for now show 0
      return null;
    }
    return null;
  };

  if (subdomainLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <div style={{ width: 40, height: 40, border: '3px solid #E63946', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#555', fontSize: 13, fontFamily: 'Manrope, sans-serif' }}>Loading facility…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (pageLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #FF2D55', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', fontFamily: 'Manrope, sans-serif' }}>
        <p style={{ fontSize: 48, marginBottom: 16 }}>🏟</p>
        <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, textTransform: 'uppercase', marginBottom: 8 }}>Facility Not Found</h1>
        <p style={{ color: '#737373', fontSize: 14 }}>The booking page you're looking for doesn't exist.</p>
      </div>
    );
  }

  const selectedCourt = courts.find(c => c.id === selectedCourtId);
  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  const pct = (countdown / (holdData?.hold_seconds || 300)) * 100;

  // ─── Render Booking Form ──────────────────────────────────────
  const applyVoucher = async (courtId, dateStr, start, end, isPlayNow) => {
    const code = voucherCode.trim().toUpperCase();
    if (!code) return;
    setVoucherBusy(true); setVoucherErr(''); setVoucherInfo(null);
    try {
      const r = await axios.post(`${BACKEND}/api/sports/public/${slug}/voucher/validate`, {
        code, court_id: courtId, date: dateStr, start_time: start, end_time: end, play_now: !!isPlayNow,
      });
      setVoucherInfo(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setVoucherErr(typeof d === 'object' ? d.message : (d || 'Invalid voucher'));
    } finally { setVoucherBusy(false); }
  };

  const renderBookingForm = (courtId, dateStr, start, end, amount, isPlayNow = false) => {
    // Load equipment availability lazily when form is first shown
    const courtSport = courts.find(c => c.id === courtId)?.sport;
    if (equipmentAvail === null && !equipmentLoading && courtSport && start && end) {
      loadEquipment(courtId, dateStr, start, end);
    }

    return (
    <div data-testid="sports-public-booking-form">
      <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text, marginBottom: 4 }}>Your Details</h3>
      <p style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>
        {courts.find(c=>c.id===courtId)?.name} · {dateStr} · {fmt12(start)}–{fmt12(end)}
        {amount != null && <strong style={{ color: T.primary }}> · ₱{amount.toLocaleString()}</strong>}
      </p>
      {['name', 'email', 'phone', 'address'].map(k => (
        <label key={k} style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 6 }}>
            {k.charAt(0).toUpperCase()+k.slice(1)}{k === 'name' || k === 'phone' ? ' *' : ''}
          </span>
          <input value={customer[k]} onChange={e => setCustomer(p=>({...p,[k]:e.target.value}))}
            data-testid={`sports-public-customer-${k}`}
            placeholder={k === 'name' ? 'Full name' : k === 'email' ? 'you@email.com' : k === 'phone' ? '+63 9XX XXX XXXX' : 'Optional'}
            style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 8, padding: '10px 14px', outline: 'none', fontFamily: 'Manrope, sans-serif', fontSize: 14, boxSizing: 'border-box' }}
          />
          {k === 'email' && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.primary, marginTop: 5, fontWeight: 700 }}>
              <AlertCircle style={{ width: 12, height: 12, flexShrink: 0 }} /> Your booking confirmation is sent here — add it so you don't miss it.
            </span>
          )}
        </label>
      ))}

      {/* Voucher code */}
      <div style={{ marginBottom: 20 }}>
        <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 6 }}>Voucher code (optional)</span>
        {voucherInfo ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#00E67614', border: '1px solid #00E67555', borderRadius: 8, padding: '10px 12px' }}>
            <div>
              <p style={{ fontSize: 13, fontWeight: 800, color: '#00E676', fontFamily: 'Oswald, sans-serif' }}>{voucherInfo.code} · {voucherInfo.label}</p>
              <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                {voucherInfo.free_minutes ? `Free +${voucherInfo.free_minutes} min → ends ${fmt12(voucherInfo.extended_end)}` : `−₱${Number(voucherInfo.discount_amount).toLocaleString()} → total ₱${Number(voucherInfo.new_amount).toLocaleString()}`}
              </p>
            </div>
            <button onClick={() => { setVoucherInfo(null); setVoucherCode(''); }} data-testid="sports-public-voucher-clear" style={{ background: 'none', border: 'none', color: T.subtle, cursor: 'pointer' }}><X style={{ width: 16, height: 16 }} /></button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={voucherCode} onChange={e => setVoucherCode(e.target.value.toUpperCase())} data-testid="sports-public-voucher-input"
                placeholder="e.g. PLAY30-A3K9"
                style={{ flex: 1, background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 8, padding: '10px 14px', outline: 'none', fontFamily: 'Manrope, sans-serif', fontSize: 14, boxSizing: 'border-box' }} />
              <button onClick={() => applyVoucher(courtId, dateStr, start, end, isPlayNow)} disabled={voucherBusy || !voucherCode.trim()} data-testid="sports-public-voucher-apply"
                style={{ background: '#00E676', color: '#0A0A0A', border: 'none', borderRadius: 8, padding: '0 18px', fontWeight: 800, fontFamily: 'Oswald, sans-serif', cursor: 'pointer', opacity: (voucherBusy || !voucherCode.trim()) ? 0.5 : 1 }}>
                {voucherBusy ? '…' : 'Apply'}
              </button>
            </div>
            {voucherErr && <p data-testid="sports-public-voucher-error" style={{ fontSize: 11, color: '#FF2D55', marginTop: 6 }}>{voucherErr}</p>}
          </div>
        )}
      </div>

      {equipmentLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0', marginBottom: 12 }}>
          <div className="animate-spin" style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${T.primary}`, borderTopColor: 'transparent' }} />
          <span style={{ fontSize: 13, color: T.muted }}>Checking equipment availability…</span>
        </div>
      )}
      {!equipmentLoading && equipmentAvail && equipmentAvail.length > 0 && (
        <div style={{ marginBottom: 20, borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: T.surface, borderBottom: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.muted, fontWeight: 700 }}>
              Equipment Rentals (Optional)
            </p>
            <p style={{ fontSize: 11, color: T.subtle, marginTop: 2 }}>Add equipment for your session. Staff will issue on arrival.</p>
          </div>
          <div style={{ padding: '8px 0' }}>
            {equipmentAvail.map(eq => {
              const qty = selectedRentals[eq.equipment_id] || 0;
              // Find first available window
              const firstAvailWindow = eq.windows.find(w => w.available_qty > 0);
              const maxAvail = firstAvailWindow?.available_qty || 0;
              const isPartial = !eq.available_for_full_duration && firstAvailWindow;
              const isUnavailable = eq.min_available === 0;
              const priceLabel = eq.rental_price > 0
                ? `₱${eq.rental_price.toLocaleString()} / ${eq.rental_price_type === 'per_hour' ? 'hr' : 'session'}`
                : 'Free';

              return (
                <div key={eq.equipment_id} data-testid={`public-rental-item-${eq.equipment_id}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: isUnavailable ? T.subtle : T.text }}>{eq.name}</p>
                      <span style={{ fontSize: 10, fontWeight: 700, color: eq.rental_price > 0 ? T.primary : '#555', background: eq.rental_price > 0 ? T.primary + '18' : '#1A1A1A', padding: '1px 6px', borderRadius: 4 }}>
                        {priceLabel}
                      </span>
                    </div>
                    {isUnavailable ? (
                      <p style={{ fontSize: 11, color: '#FF2D55', marginTop: 2 }}>Not available for this time slot</p>
                    ) : isPartial ? (
                      <p style={{ fontSize: 11, color: '#FFB300', marginTop: 2 }}>
                        Available from {fmt12(firstAvailWindow.start)}–{fmt12(firstAvailWindow.end)} ({maxAvail} {eq.unit}{maxAvail !== 1 ? 's' : ''})
                      </p>
                    ) : (
                      <p style={{ fontSize: 11, color: T.subtle, marginTop: 2 }}>
                        {maxAvail} {eq.unit}{maxAvail !== 1 ? 's' : ''} available
                      </p>
                    )}
                  </div>
                  {!isUnavailable && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button onClick={() => setSelectedRentals(r => ({ ...r, [eq.equipment_id]: Math.max(0, (r[eq.equipment_id] || 0) - 1) }))}
                        disabled={qty === 0}
                        style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.text, cursor: qty === 0 ? 'not-allowed' : 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: qty === 0 ? 0.3 : 1 }}>
                        −
                      </button>
                      <span style={{ fontSize: 15, fontWeight: 700, color: qty > 0 ? T.primary : T.muted, minWidth: 20, textAlign: 'center' }}>{qty}</span>
                      <button onClick={() => setSelectedRentals(r => ({ ...r, [eq.equipment_id]: Math.min(maxAvail, (r[eq.equipment_id] || 0) + 1) }))}
                        disabled={qty >= maxAvail}
                        data-testid={`public-rental-add-${eq.equipment_id}`}
                        style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: qty >= maxAvail ? T.surface : T.primary + '18', color: qty >= maxAvail ? T.subtle : T.primary, cursor: qty >= maxAvail ? 'not-allowed' : 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: qty >= maxAvail ? 0.4 : 1 }}>
                        +
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {Object.values(selectedRentals).some(q => q > 0) && (
            <div style={{ padding: '10px 14px', background: T.primary + '10', borderTop: `1px solid ${T.primary}30` }}>
              {(() => {
                // Calculate rental total for display
                let rentalTotal = 0;
                const lines = Object.entries(selectedRentals).filter(([,q]) => q > 0).map(([id, q]) => {
                  const eq = equipmentAvail.find(e => e.equipment_id === id);
                  if (!eq) return null;
                  const window = (eq.windows || []).find(w => w.available_qty >= q);
                  const rentStartMin = window ? t2m(window.start) : null;
                  const rentEndMin = window ? t2m(window.end) : null;
                  let hrs = rentStartMin != null ? (rentEndMin - rentStartMin) / 60 : 0;
                  const itemTotal = eq.rental_price > 0
                    ? (eq.rental_price_type === 'per_hour' ? eq.rental_price * q * hrs : eq.rental_price * q)
                    : 0;
                  rentalTotal += itemTotal;
                  return { eq, q, itemTotal };
                }).filter(Boolean);
                return (
                  <div>
                    {lines.map(({ eq, q, itemTotal }, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <p style={{ fontSize: 12, color: T.muted }}>{q}× {eq.name}</p>
                        <p style={{ fontSize: 12, color: itemTotal > 0 ? T.primary : T.subtle, fontWeight: 600 }}>
                          {itemTotal > 0 ? `₱${itemTotal.toLocaleString()}` : 'Free'}
                        </p>
                      </div>
                    ))}
                    {rentalTotal > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTop: `1px solid ${T.primary}30` }}>
                        <p style={{ fontSize: 12, fontWeight: 700, color: T.primary }}>Rental Total</p>
                        <p style={{ fontSize: 12, fontWeight: 700, color: T.primary }}>₱{rentalTotal.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {error && <p style={{ color: '#FF2D55', fontSize: 13, marginBottom: 12 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => { setStep('idle'); setError(''); setEquipmentAvail(null); setSelectedRentals({}); setPlayNowSel(false); }}
          style={{ flex: 1, background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px', fontSize: 14, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
          Back
        </button>
        <button onClick={() => initiateHold(courtId, dateStr, start, end, isPlayNow)} disabled={booking_holding}
          data-testid="sports-public-reserve-btn"
          style={{ flex: 2, background: T.gradient, color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: booking_holding ? 'not-allowed' : 'pointer', opacity: booking_holding ? 0.7 : 1, fontFamily: 'Oswald, sans-serif', boxShadow: `0 0 20px ${T.glow}` }}>
          {booking_holding ? 'Reserving…' : isPlayNow ? 'Play Now — Reserve' : 'Reserve for 5 Minutes'}
        </button>
      </div>
    </div>
    );
  };

  // ─── Timer screen ─────────────────────────────────────────────
  const renderTimer = () => (
    <div data-testid="sports-public-timer" style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, color: T.muted, marginBottom: 8 }}>Slot Reserved — Pay Now</p>
      <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 20px' }}>
        <svg width="120" height="120" style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
          <circle cx="60" cy="60" r="52" fill="none" stroke={T.border} strokeWidth="6" />
          <circle cx="60" cy="60" r="52" fill="none" stroke={T.primary} strokeWidth="6"
            strokeDasharray={`${2 * Math.PI * 52}`} strokeDashoffset={`${2 * Math.PI * 52 * (1 - pct / 100)}`}
            style={{ transition: 'stroke-dashoffset 1s linear' }} strokeLinecap="round" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 28, fontWeight: 900, fontFamily: 'Oswald, sans-serif', color: countdown <= 60 ? '#FF2D55' : T.primary, lineHeight: 1 }}>
            {mins}:{String(secs).padStart(2,'0')}
          </span>
          <span style={{ fontSize: 10, color: T.subtle, textTransform: 'uppercase', letterSpacing: 1 }}>left</span>
        </div>
      </div>
      <p style={{ fontSize: 14, color: T.text, fontWeight: 700, marginBottom: 4 }}>
        ₱{(holdData?.amount || 0).toLocaleString()} deposit required
      </p>

      {error && (
        <div data-testid="sports-public-pay-error"
          style={{ background: '#FF2D5514', border: '1px solid #FF2D5555', borderRadius: 10, padding: 12, margin: '10px 0', textAlign: 'left' }}>
          <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#FF2D55', fontWeight: 700, marginBottom: 4 }}>Payment couldn't start</p>
          <p style={{ fontSize: 13, color: '#fff', lineHeight: 1.4 }}>{error}</p>
        </div>
      )}

      {/* Per-court payment / refund disclaimer */}
      {(() => {
        const disc = (facility?.courts || []).find(c => c.id === (selectedCourtId || selectedOption?.court_id))?.payment_disclaimer;
        return disc ? (
          <div data-testid="sports-public-pay-disclaimer" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, margin: '4px 0 16px', textAlign: 'left' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 4 }}>Booking & Refund Policy</p>
            <p style={{ fontSize: 12, color: T.muted, whiteSpace: 'pre-wrap' }}>{disc}</p>
          </div>
        ) : null;
      })()}

      {(facility?.payments?.provider === 'paymongo' && facility?.payments?.enabled) ? (
        <div style={{ marginBottom: 12, marginTop: 8 }}>
          <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 8 }}>Pay online now (QR Ph / e-wallet)</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(() => {
              // Mirror backend fee math (sports_booking.py public_pay_checkout):
              // service fee applies to BOTH full and 50% deposit charges.
              const feePct = parseFloat(facility.payments.fee_percent || 0);
              const fullCharge = Math.round(holdData?.amount || 0);
              const fullFee = feePct > 0 ? Math.round(fullCharge * feePct / 100) : 0;
              const depCharge = Math.round((holdData?.amount || 0) * 0.5);
              const depFee = feePct > 0 ? Math.round(depCharge * feePct / 100) : 0;
              return (
                <>
                  {(facility.payments.options || []).includes('full') && (
                    <button onClick={() => payOnline('full')} disabled={payingOnline} data-testid="sports-public-pay-full-btn"
                      style={{ background: '#00E676', color: '#0A0A0A', border: 'none', borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 800, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', cursor: 'pointer', opacity: payingOnline ? 0.6 : 1 }}>
                      {payingOnline ? 'Redirecting…' : `Pay 100% — ₱${(fullCharge + fullFee).toLocaleString()}${fullFee > 0 ? ` (incl. ₱${fullFee.toLocaleString()} fee)` : ''}`}
                    </button>
                  )}
                  {(facility.payments.options || []).includes('deposit50') && (
                    <button onClick={() => payOnline('deposit50')} disabled={payingOnline} data-testid="sports-public-pay-deposit-btn"
                      style={{ background: 'transparent', color: '#00E676', border: '1.5px solid #00E67655', borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 800, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', cursor: 'pointer', opacity: payingOnline ? 0.6 : 1 }}>
                      Pay 50% deposit — ₱{(depCharge + depFee).toLocaleString()}{depFee > 0 ? ` (incl. ₱${depFee.toLocaleString()} fee)` : ''}
                    </button>
                  )}
                </>
              );
            })()}
            {(facility.payments.options || []).includes('counter') && facility?.payment_mode !== 'pay_first' && (
              <button onClick={payAtCourt} disabled={reserving} data-testid="sports-public-pay-court-btn"
                style={{ background: 'transparent', color: T.muted, border: `1.5px solid ${T.border}`, borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 800, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', cursor: 'pointer', opacity: reserving ? 0.6 : 1 }}>
                {reserving ? 'Reserving…' : 'Pay at Court — Reserve Now'}
              </button>
            )}
          </div>
          <p style={{ fontSize: 11, color: T.subtle, marginTop: 8 }}>You'll be redirected to a secure PayMongo page.</p>
          <button onClick={resetFlow} style={{ marginTop: 10, background: 'none', border: 'none', color: T.subtle, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </div>
      ) : (
        <>
          {holdData?.gcash?.name && (
            <p style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
              Pay via GCash to: <strong style={{ color: T.text }}>{holdData.gcash.name}</strong>
            </p>
          )}
          {gcashQrUrl && (
            <div style={{ display: 'inline-block', background: '#fff', padding: 12, borderRadius: 12, marginBottom: 20 }}>
              <img src={gcashQrUrl} alt="GCash QR" style={{ width: 180, height: 180, objectFit: 'contain', display: 'block' }} />
            </div>
          )}
          {!gcashQrUrl && holdData?.gcash?.name && (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 20, textAlign: 'left' }}>
              <p style={{ fontSize: 13, color: T.muted }}>Open GCash → Send Money → Enter <strong style={{ color: T.text }}>{holdData.gcash.name}</strong> → Amount: <strong style={{ color: T.primary }}>₱{(holdData?.amount||0).toLocaleString()}</strong></p>
            </div>
          )}
          <button onClick={() => setStep('proof')} data-testid="sports-public-paid-btn"
            style={{ width: '100%', background: T.gradient, color: '#fff', border: 'none', borderRadius: 8, padding: '13px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', boxShadow: `0 0 20px ${T.glow}`, marginBottom: 10 }}>
            I Paid — Upload Screenshot
          </button>
          {/* "Reserve & pay at court" fallback — only when counter is allowed AND we're not in pay_first mode */}
          {(facility?.payments?.options || []).includes('counter') && facility?.payment_mode !== 'pay_first' && (
            <button onClick={payAtCourt} disabled={reserving} data-testid="sports-public-pay-court-btn"
              style={{ width: '100%', background: 'transparent', color: T.muted, border: `1px solid ${T.border}`, borderRadius: 8, padding: '11px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', marginBottom: 10 }}>
              {reserving ? 'Reserving…' : 'Or reserve & pay at court'}
            </button>
          )}
          <button onClick={resetFlow} style={{ background: 'none', border: 'none', color: T.subtle, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </>
      )}
    </div>
  );

  // ─── Proof upload ─────────────────────────────────────────────
  const renderProof = () => (
    <div data-testid="sports-public-proof-upload">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text }}>Upload GCash Screenshot</h3>
        <span style={{ fontSize: 12, color: countdown <= 60 ? '#FF2D55' : T.muted, fontWeight: 700 }}>
          <Clock style={{ width: 12, height: 12, display: 'inline', marginRight: 4 }} />
          {mins}:{String(secs).padStart(2,'0')}
        </span>
      </div>
      <p style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>Attach a screenshot of your GCash payment confirmation.</p>

      <div
        onClick={() => fileRef.current?.click()}
        style={{ border: `2px dashed ${proofFile ? T.primary : T.border}`, borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer', background: proofFile ? T.primary + '08' : T.surface, transition: 'all 0.2s', marginBottom: 16 }}>
        {proofFile ? (
          <div>
            <CheckCircle style={{ width: 28, height: 28, color: T.primary, margin: '0 auto 8px' }} />
            <p style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{proofFile.name}</p>
            <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Click to change</p>
          </div>
        ) : (
          <div>
            <Upload style={{ width: 28, height: 28, color: T.subtle, margin: '0 auto 8px' }} />
            <p style={{ fontSize: 13, color: T.muted }}>Tap to select screenshot</p>
            <p style={{ fontSize: 11, color: T.subtle, marginTop: 4 }}>JPG, PNG, WEBP accepted</p>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" style={{ display: 'none' }}
          onChange={e => setProofFile(e.target.files?.[0] || null)}
          data-testid="sports-public-proof-file-input" />
      </div>

      {error && <p style={{ color: '#FF2D55', fontSize: 13, marginBottom: 12 }}>{error}</p>}

      <button onClick={handleProofUpload} disabled={!proofFile || uploading || submitting}
        data-testid="sports-public-submit-proof-btn"
        style={{ width: '100%', background: proofFile && !uploading ? T.gradient : T.surface, color: proofFile ? '#fff' : T.subtle, border: 'none', borderRadius: 8, padding: '13px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: proofFile && !uploading ? 'pointer' : 'not-allowed', fontFamily: 'Oswald, sans-serif', marginBottom: 10, opacity: (!proofFile || uploading) ? 0.6 : 1 }}>
        {uploading || submitting ? 'Submitting…' : 'Submit Booking'}
      </button>
      <button onClick={() => setStep('timer')} style={{ background: 'none', border: 'none', color: T.subtle, fontSize: 13, cursor: 'pointer' }}>← Back</button>
    </div>
  );

  // ─── Done ─────────────────────────────────────────────────────
  const renderDone = () => {
    const checkinUrl = holdData?.booking_id
      ? `${window.location.origin}/sports/checkin/${holdData.booking_id}`
      : null;
    const awaiting = doneKind === 'proof';
    const isPayOnArrival = doneKind === 'reserved' || doneKind === 'confirmed'
      || holdData?.payment_mode === 'pay_on_arrival' || holdData?.confirmed;
    const calUrl = holdData?.date ? googleCalUrl({
      title: `${holdData.court_name || 'Court'} — ${facility?.facility?.name || 'Court booking'}`,
      date: holdData.date, start: holdData.start_time, end: holdData.end_time,
      location: facility?.facility?.name || '',
    }) : null;
    const chip = { fontSize: 10, fontWeight: 900, letterSpacing: 1, padding: '4px 10px', borderRadius: 999, textTransform: 'uppercase', display: 'inline-block' };
    const rowStyle = { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderTop: `1px dashed ${T.border}`, textAlign: 'left' };
    const labelStyle = { fontSize: 10, letterSpacing: 1, color: T.subtle, textTransform: 'uppercase', fontWeight: 700 };
    const valueStyle = { fontSize: 14, color: T.text, fontWeight: 600, marginTop: 2 };
    const ghostBtn = { flex: 1, minWidth: 140, background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' };
    return (
      <div data-testid="sports-public-done" style={{ textAlign: 'center', padding: '20px 0' }}>
        {awaiting ? (
          <>
            <span style={{ ...chip, background: '#FFB30018', color: '#FFB300' }} data-testid="sports-public-done-chip">Awaiting Verification</span>
            <Clock style={{ width: 48, height: 48, color: '#FFB300', margin: '14px auto 12px' }} />
            <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, textTransform: 'uppercase', color: T.text, marginBottom: 6 }}>
              Payment Proof Submitted!
            </h3>
            <p style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>
              The owner will verify your GCash payment. You&apos;ll receive a confirmation email once it clears.
            </p>
          </>
        ) : (
          <>
            <span style={{ ...chip, background: `${T.primary}18`, color: T.primary }} data-testid="sports-public-done-chip">
              {isPayOnArrival ? 'Confirmed' : 'Submitted'}
            </span>
            <CheckCircle style={{ width: 48, height: 48, color: T.primary, margin: '14px auto 12px' }} />
            <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, textTransform: 'uppercase', color: T.text, marginBottom: 6 }}>
              {isPayOnArrival ? 'Booking Confirmed!' : 'Booking Submitted!'}
            </h3>
            <p style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>
              {isPayOnArrival
                ? 'Your court is reserved. Payment will be collected when you arrive.'
                : 'Awaiting confirmation from the court owner.'}
            </p>
          </>
        )}

        {(holdData?.court_name || holdData?.date) && (
          <div data-testid="sports-public-done-details" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '6px 16px', margin: '0 auto 20px', maxWidth: 340 }}>
            {holdData?.court_name && (
              <div style={{ ...rowStyle, borderTop: 'none' }}>
                <MapPin style={{ width: 15, height: 15, color: T.primary, marginTop: 2, flexShrink: 0 }} />
                <div><div style={labelStyle}>Court</div><div style={valueStyle}>{holdData.court_name}</div></div>
              </div>
            )}
            {holdData?.date && (
              <div style={rowStyle}>
                <CalendarDays style={{ width: 15, height: 15, color: T.primary, marginTop: 2, flexShrink: 0 }} />
                <div><div style={labelStyle}>Date</div><div style={valueStyle}>{fmtDateChip(holdData.date)}</div></div>
              </div>
            )}
            {(holdData?.start_time || holdData?.end_time) && (
              <div style={rowStyle}>
                <Clock style={{ width: 15, height: 15, color: T.primary, marginTop: 2, flexShrink: 0 }} />
                <div><div style={labelStyle}>Time</div><div style={valueStyle}>{fmt12(holdData.start_time)}{holdData.end_time ? ` — ${fmt12(holdData.end_time)}` : ''}</div></div>
              </div>
            )}
            {Number(holdData?.amount) > 0 && (
              <div style={rowStyle}>
                <Trophy style={{ width: 15, height: 15, color: T.primary, marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={labelStyle}>{awaiting ? 'Amount Sent' : 'Amount'}</div>
                  <div style={valueStyle}>
                    ₱{Number(holdData.amount).toLocaleString()}
                    <span style={{ color: awaiting ? '#FFB300' : T.subtle, fontSize: 11, marginLeft: 6, fontWeight: 600 }}>
                      {awaiting ? '· pending verification' : isPayOnArrival ? '· due at the court' : ''}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {checkinUrl && (
          <div style={{ margin: '0 auto 20px', maxWidth: 220 }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: T.subtle, marginBottom: 6, fontWeight: 700 }}>
              Your Booking QR
            </p>
            {holdData?.ref_number && (
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 900, color: T.primary, letterSpacing: 2, marginBottom: 10 }}>
                {holdData.ref_number}
              </p>
            )}
            <div style={{ background: '#fff', borderRadius: 12, padding: 14, display: 'inline-block', boxShadow: `0 0 24px ${T.glow}` }}>
              <QRCodeSVG value={checkinUrl} size={160} bgColor="#ffffff" fgColor="#0A0A0A" level="H" includeMargin={true} />
            </div>
            <p style={{ fontSize: 11, color: T.subtle, marginTop: 8 }}>Show this to court staff when you arrive</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', margin: '0 auto 16px', maxWidth: 340 }}>
          {calUrl && (
            <a href={calUrl} target="_blank" rel="noreferrer" data-testid="sports-public-done-calendar-btn" style={ghostBtn}>
              <CalendarPlus style={{ width: 14, height: 14 }} /> Add to Calendar
            </a>
          )}
          {checkinUrl && (
            <button data-testid="sports-public-done-copy-btn" style={ghostBtn}
              onClick={() => { navigator.clipboard?.writeText(checkinUrl); toast_style('Check-in link copied'); }}>
              <Copy style={{ width: 14, height: 14 }} /> Copy Check-in Link
            </button>
          )}
        </div>

        <button onClick={resetFlow} data-testid="sports-public-book-another-btn"
          style={{ background: T.gradient, color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
          Book Another Slot
        </button>
      </div>
    );
  };

  // ─── Expired ──────────────────────────────────────────────────
  const renderExpired = () => (
    <div data-testid="sports-public-expired" style={{ textAlign: 'center', padding: '20px 0' }}>
      <AlertCircle style={{ width: 52, height: 52, color: '#FF2D55', margin: '0 auto 16px' }} />
      <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, textTransform: 'uppercase', color: '#fff', marginBottom: 8 }}>Hold Expired</h3>
      <p style={{ fontSize: 14, color: T.muted, marginBottom: 24 }}>Your 5-minute hold timed out. The slot has been released. Please select a time again.</p>
      <button onClick={resetFlow} style={{ background: T.gradient, color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
        Try Again
      </button>
    </div>
  );

  // ─── Main page layout ─────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: 'Manrope, sans-serif', overflowX: 'hidden', width: '100%' }}>
      <style>{`@keyframes agPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      {/* Toast */}
      {toastMsg && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#222', color: '#fff', borderRadius: 8, padding: '10px 20px', fontSize: 13, zIndex: 9999, border: '1px solid #333' }}>{toastMsg}</div>
      )}

      {/* Header */}
      <header style={{ background: T.bg + 'EE', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            onClick={() => navigatePage('home')}>
            {facility?.logo_url ? (
              <img src={facility.logo_url} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
            ) : (
              <div style={{ width: 36, height: 36, borderRadius: 8, background: T.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 16px ${T.glow}` }}>
                <SportIcon sport={uniqueSports[0] || 'other'} size={20} color={T.accd} />
              </div>
            )}
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: T.text, lineHeight: 1 }}>
              {facility?.facility?.name || slug}
            </p>
          </div>
          {/* Mini-site nav: Home | Events tabs; "Book Now" pill IS the booking nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <nav style={{ display: 'flex', alignItems: 'center', gap: 2 }} data-testid="public-site-nav">
              {[['home', 'Home', 'public-nav-home'], ['tournaments', 'Events', 'public-nav-tournaments']].map(([key, label, tid]) => (
                <button key={key} onClick={() => navigatePage(key)} data-testid={tid}
                  style={{ padding: '8px 14px', background: activePage === key ? T.primary + '18' : 'transparent', color: activePage === key ? T.primary : T.muted, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'Oswald, sans-serif', transition: 'all 0.15s' }}>
                  {label}
                </button>
              ))}
            </nav>
            <button onClick={() => navigatePage('booking')} data-testid="public-nav-booking"
              style={{ background: T.gradient, color: T.accd, border: 'none', borderRadius: 999, padding: '8px 20px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', boxShadow: activePage === 'booking' ? 'none' : `0 0 14px ${T.glow}`, opacity: activePage === 'booking' ? 0.85 : 1 }}>
              Book Now
            </button>
          </div>
        </div>
      </header>

      {/* Announcement — pinned on every page */}
      {siteBuilder.announcement?.enabled && siteBuilder.announcement?.text && (
        <div data-testid="public-announcement-bar" style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Megaphone style={{ width: 14, height: 14, color: T.primary, flexShrink: 0 }} />
            <p style={{ fontSize: 12.5, color: T.text, lineHeight: 1.5 }}>{siteBuilder.announcement.text}</p>
          </div>
        </div>
      )}

      {/* ═══ HOME PAGE ═══ */}
      {hasMultiPage && activePage === 'home' && (
        <div data-testid="public-page-home" style={{ position: 'relative' }}>
          {/* Ambient accent glow — the depth that makes the dark canvas premium */}
          <div aria-hidden="true" style={{ position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)', width: 'min(1100px, 100vw)', height: 480, background: `radial-gradient(55% 55% at 50% 0%, ${T.glow} 0%, transparent 70%)`, pointerEvents: 'none' }} />
          {/* Hero — gradient display headline, real social proof, punchy CTAs */}
          <section style={{ position: 'relative', padding: '52px 20px 36px', maxWidth: 900, margin: '0 auto' }}>
            {(facility?.stats?.bookings_served || 0) >= 30 && (
              <span data-testid="public-social-proof-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${T.primary}40`, background: T.primary + '10', borderRadius: 999, padding: '5px 12px', fontSize: 11.5, color: T.text, marginBottom: 14 }}>
                <Users style={{ width: 12, height: 12, color: T.primary }} /> {facility.stats.bookings_served.toLocaleString()}+ bookings served here
              </span>
            )}
            <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(34px, 6vw, 60px)', fontWeight: 900, textTransform: 'uppercase', lineHeight: 1.02, marginBottom: 8, background: T.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', maxWidth: 700 }}>
              {siteBuilder.home_headline || facility?.facility?.name || slug}
            </h1>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(18px, 2.6vw, 24px)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: T.text, marginBottom: 14 }}>
              Book your game in <span style={{ color: T.primary }}>seconds</span>
            </p>
            <p style={{ fontSize: 14, color: T.muted, maxWidth: 520, lineHeight: 1.7, marginBottom: 22 }}>
              {siteBuilder.home_description || facility?.about || 'Live availability, fair pricing. Pay online or at the counter.'}
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => navigatePage('booking')} data-testid="public-hero-book-cta"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.gradient, color: T.accd, border: 'none', borderRadius: 999, padding: '13px 28px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', boxShadow: `0 0 24px ${T.glow}` }}>
                <CalendarDays style={{ width: 15, height: 15 }} /> Book a court
              </button>
              {(activeTournaments.length > 0 || pubOpenPlay.length > 0) && (
                <button onClick={() => navigatePage('tournaments')} data-testid="public-hero-events-cta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.primary + '14', color: T.text, border: `1px solid ${T.primary}55`, borderRadius: 999, padding: '13px 28px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
                  <Trophy style={{ width: 15, height: 15, color: T.primary }} /> Join an event
                </button>
              )}
              {facility?.contact && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.muted }}>
                  <Phone style={{ width: 14, height: 14, color: T.primary }} /> {facility.contact}
                </span>
              )}
            </div>
            {/* Hero photo strip — swipe / drag / arrows, gentle auto-drift */}
            {heroStrip.length > 0 && (
              <HeroCarousel items={heroStrip} T={T}
                onOpen={(idx) => setLightbox({ list: heroStrip, idx })} />
            )}
          </section>

          {/* Play today — live next-slot chips */}
          {playToday.length > 0 && !bookingNotice?.booking_disabled && (
            <section data-testid="public-play-today" style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px 26px' }}>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: T.text, marginBottom: 10 }}>Play today</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {playToday.map((o, i) => (
                  <button key={`${o.court_id}-${o.start}`} onClick={() => jumpToSlot(o)} data-testid={`play-today-chip-${i}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: i === 0 ? T.primary + '14' : T.surface, border: `1px solid ${i === 0 ? T.primary : T.border}`, borderRadius: 999, padding: '8px 15px', fontSize: 12.5, color: T.text, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
                    <Clock style={{ width: 12, height: 12, color: T.primary }} />
                    {fmt12(o.start)} · {o.court_name} · ₱{o.amount}
                  </button>
                ))}
              </div>
            </section>
          )}

          <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px 60px' }}>

            {/* Courts Overview — collage of courts-tagged photos + court chips */}
            {courts.length > 0 && (
              <div style={{ marginBottom: 36 }}>
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text, marginBottom: 14 }}>Our Courts</h2>
                {courtsPhotos.length > 0 && (
                  <div data-testid="public-courts-collage"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gridAutoRows: 110, gap: 8, marginBottom: 14 }}>
                    {courtsPhotos.map((g, i) => (
                      <div key={i} onClick={() => setLightbox({ list: courtsPhotos, idx: i })} role="button" tabIndex={0}
                        style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', cursor: 'zoom-in', border: `1px solid ${T.border}`,
                          gridColumn: (i === 0 && courtsPhotos.length >= 3) ? 'span 2' : undefined,
                          gridRow: (i === 0 && courtsPhotos.length >= 3) ? 'span 2' : undefined }}>
                        <img src={g.thumb_url || g.url} alt={g.caption || ''} loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        {g.caption && (
                          <p style={{ position: 'absolute', left: 0, right: 0, bottom: 0, margin: 0, padding: '16px 10px 6px', fontSize: 11, color: '#fff', background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)' }}>
                            {g.caption}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {courts.map(c => (
                    <div key={c.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, minWidth: 180 }}>
                      <SportIcon sport={c.sport || 'other'} size={22} color={T.primary} />
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{c.name}</p>
                        <p style={{ fontSize: 11, color: T.subtle }}>{c.sport?.charAt(0).toUpperCase() + c.sport?.slice(1)} · {hoursLabel(c)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Links */}
            {Object.values(siteBuilder.social_links || {}).some(v => v) && (
              <div style={{ marginBottom: 36 }}>
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text, marginBottom: 14 }}>Follow Us</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {siteBuilder.social_links?.instagram && (
                    <a href={siteBuilder.social_links.instagram} target="_blank" rel="noreferrer" data-testid="public-social-instagram"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 16px', textDecoration: 'none', color: T.muted, fontSize: 13, transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = T.primary}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                      <Instagram className="h-4 w-4" style={{ color: '#E4405F' }} /> Instagram
                    </a>
                  )}
                  {siteBuilder.social_links?.youtube && (
                    <a href={siteBuilder.social_links.youtube} target="_blank" rel="noreferrer" data-testid="public-social-youtube"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 16px', textDecoration: 'none', color: T.muted, fontSize: 13, transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = T.primary}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                      <Youtube className="h-4 w-4" style={{ color: '#FF0000' }} /> YouTube
                    </a>
                  )}
                  {siteBuilder.social_links?.facebook && (
                    <a href={siteBuilder.social_links.facebook} target="_blank" rel="noreferrer" data-testid="public-social-facebook"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 16px', textDecoration: 'none', color: T.muted, fontSize: 13, transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = T.primary}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                      <Facebook className="h-4 w-4" style={{ color: '#1877F2' }} /> Facebook
                    </a>
                  )}
                  {siteBuilder.social_links?.tiktok && (
                    <a href={siteBuilder.social_links.tiktok} target="_blank" rel="noreferrer" data-testid="public-social-tiktok"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 16px', textDecoration: 'none', color: T.muted, fontSize: 13, transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = T.primary}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                      <Globe className="h-4 w-4" /> TikTok
                    </a>
                  )}
                  {siteBuilder.social_links?.website && (
                    <a href={siteBuilder.social_links.website} target="_blank" rel="noreferrer" data-testid="public-social-website"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 16px', textDecoration: 'none', color: T.muted, fontSize: 13, transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = T.primary}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                      <ExternalLink className="h-4 w-4" style={{ color: T.primary }} /> Website
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Upcoming Tournaments (if any, show on home too) */}
            {(activeTournaments.length > 0 || pubTournaments.length > 0) && (
              <div style={{ marginBottom: 36 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text }}>Tournaments</h2>
                  {pubTournaments.length > 0 && (
                    <button onClick={() => navigatePage('tournaments')} data-testid="home-view-all-tournaments"
                      style={{ background: 'transparent', border: 'none', color: T.primary, fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      View all ({pubTournaments.length}) →
                    </button>
                  )}
                </div>
                {activeTournaments.length === 0 ? (
                  <p style={{ fontSize: 13, color: T.subtle }}>No live or open-registration tournaments right now.</p>
                ) : (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {activeTournaments.slice(0, 3).map(tn => (
                    <a key={tn.id} href={`/book/${slug}/tournament/${tn.id}`}
                      style={{ flex: '1 1 240px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 18px', textDecoration: 'none', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = T.primary + '80'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, color: T.text }}>{tn.name}</p>
                        {tn.status === 'in_progress' && <span style={{ fontSize: 10, fontWeight: 700, color: T.primary, background: T.primary + '22', padding: '2px 8px', borderRadius: 99 }}>LIVE</span>}
                        {tn.status === 'registration_open' && <span style={{ fontSize: 10, fontWeight: 700, color: '#22C55E', background: '#22C55E22', padding: '2px 8px', borderRadius: 99 }}>REGISTER</span>}
                      </div>
                      <p style={{ fontSize: 12, color: T.muted }}>
                        {tn.event_style === 'announce'
                          ? [tn.date_start ? fmtDate(tn.date_start) : null, tn.entry_fee > 0 ? `₱${tn.entry_fee} entry` : null].filter(Boolean).join(' · ') || 'Tournament'
                          : `${tn.format === 'round_robin' ? 'Round Robin' : 'Single Elimination'} · ${tn.team_count} teams`}
                      </p>
                      {/* "Just Joined" preview strip on HOME page too */}
                      {tn.status === 'registration_open' && (tn.recent_teams?.length || 0) > 0 && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }} data-testid={`tourn-joined-home-${tn.id}`}>
                            {tn.recent_teams.slice(0, 4).map(rt => (
                              <div key={rt.id} title={rt.team_name} style={{ width: 22, height: 22 }}>
                                {rt.logo_url ? (
                                  <img src={rt.logo_url} alt={rt.team_name}
                                    style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${T.border}` }} />
                                ) : (
                                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: T.primary + '22', color: T.primary, fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${T.primary}44`, fontFamily: 'Oswald, sans-serif' }}>
                                    {(rt.team_name || '?').slice(0, 2).toUpperCase()}
                                  </div>
                                )}
                              </div>
                            ))}
                            <span style={{ fontSize: 10, color: T.subtle, marginLeft: 4 }}>
                              {tn.team_count} joined
                            </span>
                          </div>
                        </div>
                      )}
                    </a>
                  ))}
                </div>
                )}
              </div>
            )}

            {/* Open Play sessions (social rotation) */}
            {pubOpenPlay.length > 0 && (
              <div style={{ marginBottom: 36 }} data-testid="public-open-play-section">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text, marginBottom: 14 }}>Open Play Sessions</h2>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {pubOpenPlay.slice(0, 3).map(op => (
                    <OpenPlayCard key={op.id} op={op} slug={slug} T={T} />
                  ))}
                </div>
              </div>
            )}

            {/* Stats — real counts only, hidden while the facility is new */}
            {facility?.stats && ((facility.stats.bookings_served || 0) >= 30 || (facility.stats.events_hosted || 0) >= 3) && (
              <div data-testid="public-stats-band" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 36 }}>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px' }}>
                  <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 700, color: T.primary, lineHeight: 1 }}>{facility.stats.courts}</p>
                  <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Court{facility.stats.courts === 1 ? '' : 's'}</p>
                </div>
                {(facility.stats.events_hosted || 0) >= 3 && (
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px' }}>
                    <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 700, color: T.primary, lineHeight: 1 }}>{facility.stats.events_hosted}</p>
                    <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Events hosted</p>
                  </div>
                )}
                {(facility.stats.bookings_served || 0) >= 30 && (
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px' }}>
                    <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 700, color: T.primary, lineHeight: 1 }}>{facility.stats.bookings_served.toLocaleString()}+</p>
                    <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Bookings served</p>
                  </div>
                )}
              </div>
            )}

            {/* CTA */}
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <button onClick={() => navigatePage('booking')} data-testid="public-home-book-cta"
                style={{ background: T.gradient, color: T.accd, border: 'none', borderRadius: 10, padding: '14px 40px', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', boxShadow: `0 0 30px ${T.glow}` }}>
                Book a Court
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ EVENTS PAGE ═══ */}
      {hasMultiPage && activePage === 'tournaments' && (
        <div data-testid="public-page-tournaments" style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px 60px' }}>
          <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 32, fontWeight: 900, textTransform: 'uppercase', marginBottom: 8, background: T.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Events
          </h1>
          <p style={{ fontSize: 14, color: T.muted, marginBottom: 20 }}>
            {facility?.facility?.name} — tournaments, open play, and results
          </p>
          {eventsPhotos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 26 }} data-testid="public-events-photos">
              {eventsPhotos.map((g, i) => (
                <img key={i} src={g.thumb_url || g.url} alt={g.caption || ''} loading="lazy"
                  onClick={() => setLightbox({ list: eventsPhotos, idx: i })}
                  style={{ height: 92, width: 'auto', maxWidth: 180, objectFit: 'cover', borderRadius: 10, cursor: 'zoom-in', border: `1px solid ${T.border}` }} />
              ))}
            </div>
          )}
          {/* Open Play sessions (registration open / live) */}
          {pubOpenPlay.length > 0 && (
            <div style={{ marginBottom: 32 }} data-testid="tournaments-page-open-play-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Users style={{ width: 16, height: 16, color: T.primary }} />
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text }}>Open Play Sessions</h2>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {pubOpenPlay.map(op => (
                  <OpenPlayCard key={op.id} op={op} slug={slug} T={T} />
                ))}
              </div>
            </div>
          )}
          {/* Recently completed Open Play sessions — with podiums, mirrors tournament history cards */}
          {pubOpenPlayEnded.length > 0 && (
            <div style={{ marginBottom: 32 }} data-testid="tournaments-page-open-play-ended-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Trophy style={{ width: 16, height: 16, color: '#FFD54A' }} />
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text }}>Recent Open Play Results</h2>
              </div>
              <div className="space-y-4">
                {pubOpenPlayEnded.map(op => {
                  const href = `/book/${slug}/open-play/${op.id}`;
                  const podiums = Array.isArray(op.podium) ? op.podium.filter(p => p.first) : [];
                  return (
                    <div key={op.id} onClick={() => { window.location.href = href; }}
                      data-testid={`public-tournaments-page-open-play-ended-${op.id}`}
                      style={{ display: 'block', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px 22px', cursor: 'pointer', transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = T.primary + '60'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = 'none'; }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 700, color: T.text }}>{op.name}</p>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#A3A3A3', background: '#2A2A2A', padding: '3px 10px', borderRadius: 99 }}>COMPLETED</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, color: T.muted }}>
                          {op.game_mode === 'singles' ? 'Singles' : 'Doubles'} rotation
                        </span>
                        <span style={{ fontSize: 13, color: T.muted }}>{op.player_count || 0} players</span>
                        {op.date && <span style={{ fontSize: 13, color: T.muted }}>{fmtDate(op.date)}</span>}
                        {op.completed_matches > 0 && <span style={{ fontSize: 13, color: T.muted }}>{op.completed_matches} matches</span>}
                        {(op.court_names || []).length > 0 && (
                          <span style={{ fontSize: 13, color: T.muted }}>{op.court_names.join(' · ')}</span>
                        )}
                      </div>
                      {podiums.length > 0 && (
                        <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
                          {podiums.map((p, i) => (
                            <PodiumStrip key={i} p={p} T={T}
                              onShare={e => { e.stopPropagation(); shareChampionsCard({ eventName: op.name, facilityName: facility?.facility?.name, date: op.date, division: p.division, podium: p, awards: cardAwards(p.awards) }); }}
                              onDownload={e => { e.stopPropagation(); downloadChampionsCard({ eventName: op.name, facilityName: facility?.facility?.name, date: op.date, division: p.division, podium: p, awards: cardAwards(p.awards) }); }} />
                          ))}
                        </div>
                      )}
                      <a href={href} onClick={e => e.stopPropagation()} data-testid={`view-open-play-record-${op.id}`}
                        style={{ display: 'inline-block', marginTop: 12, fontSize: 12, fontWeight: 700, color: T.primary, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        View session record →
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pubTournaments.length === 0 ? (
            pubOpenPlayEnded.length === 0 && pubOpenPlay.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <Trophy style={{ width: 48, height: 48, color: T.border, margin: '0 auto 12px' }} />
                <p style={{ fontSize: 15, color: T.subtle }}>No tournaments scheduled yet</p>
                <p style={{ fontSize: 13, color: T.subtle, marginTop: 4 }}>Check back soon for upcoming events</p>
              </div>
            )
          ) : (
            <div className="space-y-4">
              {sortedTournaments.map(tn => {
                const href = `/book/${slug}/tournament/${tn.id}`;
                const podiums = (tn.status === 'completed' && Array.isArray(tn.podium)) ? tn.podium.filter(p => p.first) : [];
                return (
                <div key={tn.id} onClick={() => { window.location.href = href; }} data-testid={`public-tournaments-page-${tn.id}`}
                  style={{ display: 'block', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px 22px', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.primary + '60'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = 'none'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 700, color: T.text }}>{tn.name}</p>
                    {tn.status === 'in_progress' && <span style={{ fontSize: 11, fontWeight: 700, color: T.primary, background: T.primary + '20', padding: '3px 10px', borderRadius: 99 }}>LIVE</span>}
                    {tn.status === 'registration_open' && <span style={{ fontSize: 11, fontWeight: 700, color: '#22C55E', background: '#22C55E20', padding: '3px 10px', borderRadius: 99 }}>OPEN</span>}
                    {tn.status === 'registration_closed' && <span style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', background: '#F59E0B20', padding: '3px 10px', borderRadius: 99 }}>UPCOMING</span>}
                    {tn.status === 'completed' && <span style={{ fontSize: 11, fontWeight: 700, color: '#A3A3A3', background: '#2A2A2A', padding: '3px 10px', borderRadius: 99 }}>COMPLETED</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {tn.event_style !== 'announce' && (
                      <span style={{ fontSize: 13, color: T.muted }}>
                        {tn.format === 'round_robin' ? 'Round Robin' : tn.format === 'double_elimination' ? 'Double Elimination' : 'Single Elimination'}
                      </span>
                    )}
                    {(tn.event_style !== 'announce' || tn.announce_registration === 'onsite') && (
                      <span style={{ fontSize: 13, color: T.muted }}>{tn.team_count || 0} teams</span>
                    )}
                    {tn.date_start && <span style={{ fontSize: 13, color: T.muted }}>{fmtDate(tn.date_start)}</span>}
                    {tn.entry_fee > 0 && <span style={{ fontSize: 13, color: T.primary, fontWeight: 600 }}>₱{tn.entry_fee}</span>}
                  </div>
                  {/* Winners podium(s) for completed events */}
                  {podiums.length > 0 && (
                    <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
                      {podiums.map((p, i) => (
                        <PodiumStrip key={i} p={p} T={T} onShare={e => { e.stopPropagation(); shareChampionsCard({ eventName: tn.name, facilityName: facility?.facility?.name, date: tn.date_start, division: p.division, podium: p }); }}
                          onDownload={e => { e.stopPropagation(); downloadChampionsCard({ eventName: tn.name, facilityName: facility?.facility?.name, date: tn.date_start, division: p.division, podium: p }); }} />
                      ))}
                    </div>
                  )}
                  {tn.status === 'completed' && (
                    <a href={href} onClick={e => e.stopPropagation()} data-testid={`view-record-${tn.id}`}
                      style={{ display: 'inline-block', marginTop: 12, fontSize: 12, fontWeight: 700, color: T.primary, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      View tournament record →
                    </a>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ BOOKING PAGE (original content) ═══ */}
      {(!hasMultiPage || activePage === 'booking') && (
      <>
      {/* Booking page header — focused, no landing noise */}
      <section style={{ padding: '36px 20px 24px', maxWidth: 680, margin: '0 auto' }}>
        <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(26px, 4.5vw, 38px)', fontWeight: 900, textTransform: 'uppercase', lineHeight: 1, marginBottom: 6, background: T.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Book a court
        </h1>
        <p style={{ fontSize: 13, color: T.muted }}>
          {facility?.facility?.name || slug} — live availability, instant confirmation.
        </p>
      </section>

      {/* Play today — next open slots, straight from live availability */}
      {playToday.length > 0 && !bookingNotice?.booking_disabled && (
        <section data-testid="public-play-today" style={{ maxWidth: 680, margin: '0 auto', padding: '0 20px 22px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {playToday.map((o, i) => (
              <button key={`${o.court_id}-${o.start}`} onClick={() => jumpToSlot(o)} data-testid={`play-today-chip-${i}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: i === 0 ? T.primary + '14' : T.surface, border: `1px solid ${i === 0 ? T.primary : T.border}`, borderRadius: 999, padding: '8px 15px', fontSize: 12.5, color: T.text, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
                <Clock style={{ width: 12, height: 12, color: T.primary }} />
                {fmt12(o.start)} · {o.court_name} · ₱{o.amount}
              </button>
            ))}
          </div>
        </section>
      )}


      {/* Happening-now strip — events live on their own page, one tap away */}
      {(activeTournaments.length > 0 || pubOpenPlay.length > 0) && (
        <section data-testid="booking-open-play-section" style={{ maxWidth: 680, margin: '0 auto 22px', padding: '0 20px' }}>
          <button onClick={() => navigatePage('tournaments')} data-testid="booking-view-all-tournaments"
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: T.primary + '10', border: `1px solid ${T.primary}40`, borderRadius: 12, padding: '12px 16px', cursor: 'pointer', textAlign: 'left' }}>
            <Trophy style={{ width: 16, height: 16, color: T.primary, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 12.5, color: T.text, fontWeight: 700 }}>
              {(() => {
                const liveN = activeTournaments.filter(t => t.status === 'in_progress').length
                  + pubOpenPlay.filter(o => o.status === 'active').length;
                const soonN = activeTournaments.length + pubOpenPlay.length - liveN;
                const ev = (n) => `${n} event${n === 1 ? '' : 's'}`;
                return liveN > 0
                  ? `${ev(liveN)} happening now${soonN > 0 ? ` · ${soonN} coming up` : ''} — tournaments & open play`
                  : `${ev(soonN)} coming up — tournaments & open play`;
              })()}
            </span>
            <span style={{ fontSize: 12, color: T.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>View →</span>
          </button>
        </section>
      )}

      {/* Sport selector (shown BEFORE booking widget when multiple sports) */}
      {uniqueSports.length > 1 && !selectedSport && (
        <section style={{ maxWidth: 680, margin: '0 auto 32px', padding: '0 20px' }}>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '28px 24px' }}>
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, textTransform: 'uppercase', color: T.text, marginBottom: 6 }}>
              What sport are you booking?
            </h2>
            <p style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>Choose your sport to see available courts and rates.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {uniqueSports.map(s => (
                <button key={s} data-testid={`sports-public-select-sport-${s}`}
                  onClick={() => setSelectedSport(s)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '18px 28px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', minWidth: 110 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.primary + '80'; e.currentTarget.style.background = T.primary + '10'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bg; e.currentTarget.style.transform = 'none'; }}>
                  <span style={{ fontSize: 40, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SportIcon sport={s} size={42} color={T.primary} />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text, textTransform: 'capitalize', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </span>
                  <span style={{ fontSize: 11, color: T.muted }}>
                    {courts.filter(c => c.sport === s).length} court{courts.filter(c => c.sport === s).length > 1 ? 's' : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sport chip when selected (show change option) */}
      {selectedSport && uniqueSports.length > 1 && (
        <div style={{ maxWidth: 680, margin: '0 auto 16px', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: T.primary + '20', border: `1px solid ${T.primary + '50'}`, borderRadius: 20, padding: '5px 14px', fontSize: 13, color: T.primary, fontWeight: 700 }}>
              {selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)}
            </span>
            <button onClick={() => { setSelectedSport(null); setSmartResults(null); setSelectedCourtId(''); setStep('idle'); setError(''); }}
              data-testid="sports-public-change-sport"
              style={{ fontSize: 12, color: T.subtle, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Change sport
            </button>
          </div>
        </div>
      )}

      {/* Booking widget — only show if sport is selected (or only 1 sport) */}
      {(!selectedSport && uniqueSports.length > 1) ? null : (
      <section id="booking" style={{ maxWidth: 680, margin: '0 auto', padding: '0 20px 80px' }}>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: `0 20px 60px ${T.glow}` }}>
          {/* Tabs — only show if idle/results */}
          {['idle', 'results', 'searching'].includes(step) && (
            <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}` }}>
              {[['smart', 'Smart Booking'], ['courts', 'Browse Courts'], ['schedule', 'Schedule']].map(([k, l]) => (
                <button key={k} data-testid={`sports-public-tab-${k}`}
                  onClick={() => { setTab(k); setSmartResults(null); setError(''); setAlternatives(null); setRangeStart(null); setRangeEnd(null); }}
                  style={{ flex: 1, padding: '14px 16px', background: 'none', border: 'none', color: tab === k ? T.primary : T.subtle, fontSize: 13, fontWeight: tab === k ? 700 : 500, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', borderBottom: `2px solid ${tab === k ? T.primary : 'transparent'}`, transition: 'all 0.2s', fontFamily: 'Oswald, sans-serif' }}>
                  {l}
                </button>
              ))}
            </div>
          )}

          <div style={{ padding: '24px' }}>
            {/* ── Neutral booking-unavailable notice (never exposes billing state) ── */}
            {bookingNotice?.booking_disabled && (step === 'idle' || step === 'results') && (
              <div data-testid="public-booking-unavailable-banner"
                style={{ background: '#141414', border: '1px solid #2A2A2A', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Online booking temporarily unavailable</p>
                <p style={{ fontSize: 12, color: '#A3A3A3' }}>
                  Please contact the facility directly to reserve a court{bookingNotice.facility_contact ? '.' : ' or check back later.'}
                </p>
                {bookingNotice.facility_contact && (
                  <p style={{ fontSize: 12, color: '#E63946', fontWeight: 700, marginTop: 6 }} data-testid="public-facility-contact">
                    {bookingNotice.facility_contact}
                  </p>
                )}
              </div>
            )}
            {/* ── Booking flow states ── */}
            {step === 'form' && selectedOption && renderBookingForm(selectedOption.court_id, selectedOption.date, selectedOption.start, selectedOption.end, selectedOption.amount)}
            {step === 'form' && !selectedOption && playNowSel && slotData?.play_now && renderBookingForm(selectedCourtId, courtDate, slotData.play_now.start, pointTime(slotData, rangeEnd != null ? rangeEnd : pnIdx(slotData) + 1) || slotData.play_now.slot_end, pnAmount != null ? pnAmount : slotData.play_now.price, true)}
            {step === 'form' && !selectedOption && !playNowSel && rangeStart !== null && rangeEnd !== null && renderBookingForm(selectedCourtId, courtDate, slotData?.slots?.[rangeStart]?.start, pointTime(slotData, rangeEnd), null)}
            {step === 'timer' && renderTimer()}
            {step === 'proof' && renderProof()}
            {step === 'done' && renderDone()}
            {step === 'expired' && renderExpired()}

            {/* ── Smart Booking ── */}
            {(step === 'idle' || step === 'results') && tab === 'smart' && !bookingNotice?.booking_disabled && (
              <div data-testid="sports-public-smart-tab">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text, marginBottom: 14 }}>Book a Slot</h2>

                {/* Collapsible calendar date picker */}
                <div style={{ marginBottom: 12 }}>
                  <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 6 }}>Date</span>
                  {calOpen ? (
                    <BookingCalendar
                      value={date}
                      onChange={d => { setDate(d); setSmartResults(null); setCalOpen(false); }}
                      slug={slug}
                      T={T}
                      maxDate={maxDate}
                      minDate={todayLocal}
                    />
                  ) : (
                    /* Collapsed date chip — click to re-open */
                    <button onClick={() => setCalOpen(true)} data-testid="sports-public-smart-date"
                      style={{ width: '100%', background: T.surface, border: `1.5px solid ${T.primary}60`, borderRadius: 10, padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary, display: 'inline-block', flexShrink: 0 }} />
                        <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
                          {fmtDateChip(date)}
                        </span>
                      </div>
                      <span style={{ fontSize: 10, color: T.subtle, textTransform: 'uppercase', letterSpacing: 1 }}>Change</span>
                    </button>
                  )}
                </div>

                {/* Sport (when multi-sport and no sport pre-selected) */}
                {uniqueSports.length > 1 && (
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 6 }}>
                      Sport {selectedSport ? '' : '(optional)'}
                    </span>
                    {selectedSport ? (
                      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: T.primary, fontWeight: 700 }}>
                        {selectedSport.charAt(0).toUpperCase()+selectedSport.slice(1)}
                      </div>
                    ) : (
                      <select value={sportFilter} onChange={e => setSportFilter(e.target.value)}
                        data-testid="sports-public-smart-sport"
                        onWheel={e => e.target.blur()}
                        style={{ width: '100%', background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 8, padding: '10px 12px', outline: 'none', fontFamily: 'Manrope, sans-serif', fontSize: 13, boxSizing: 'border-box' }}>
                        <option value="">Any sport</option>
                        {uniqueSports.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                      </select>
                    )}
                  </div>
                )}

                {/* Time selectors — onWheel blur prevents accidental scroll changes */}
                <div style={{ marginBottom: 16 }}>
                  {/* Hint when today is selected and past times are filtered */}
                  {date === today() && minStartMin > 0 && (
                    <p style={{ fontSize: 10, color: T.subtle, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                      Showing available times from now
                    </p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <label>
                      <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 6 }}>Start Time</span>
                      <select value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="sports-public-smart-start"
                        onWheel={e => e.target.blur()}
                        style={{ width: '100%', background: T.bg, border: `1px solid ${T.border}`, color: startTime ? T.text : T.subtle, borderRadius: 8, padding: '10px 12px', outline: 'none', fontFamily: 'Manrope, sans-serif', fontSize: 13, boxSizing: 'border-box' }}>
                        <option value="">Select…</option>
                        {validStartTimes.map(t => (
                          <option key={t} value={t}>{fmt12(t)}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 6 }}>End Time</span>
                      <select value={endTime} onChange={e => setEndTime(e.target.value)} data-testid="sports-public-smart-end"
                        onWheel={e => e.target.blur()}
                        style={{ width: '100%', background: T.bg, border: `1px solid ${T.border}`, color: endTime ? T.text : T.subtle, borderRadius: 8, padding: '10px 12px', outline: 'none', fontFamily: 'Manrope, sans-serif', fontSize: 13, boxSizing: 'border-box' }}>
                        <option value="">{startTime ? 'Select end…' : 'Select…'}</option>
                        {validEndTimes.map(t => (
                          <option key={t} value={t}>{fmt12(t)}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {smartMinNeeded > 30 && startTime && (
                    <p style={{ fontSize: 12, color: T.subtle, marginTop: 8 }} data-testid="sports-public-min-duration-hint">
                      ⏱ Bookings starting at {fmt12(startTime)} require at least{' '}
                      {Math.floor(smartMinNeeded / 60) ? `${Math.floor(smartMinNeeded / 60)} hour${Math.floor(smartMinNeeded / 60) > 1 ? 's' : ''}` : ''}
                      {Math.floor(smartMinNeeded / 60) && smartMinNeeded % 60 ? ' ' : ''}
                      {smartMinNeeded % 60 ? `${smartMinNeeded % 60} minutes` : ''}.
                    </p>
                  )}
                </div>

                {error && <p style={{ color: '#FF2D55', fontSize: 13, marginBottom: 12 }}>{error}</p>}

                <button onClick={handleSmartSearch} disabled={searching || !!bookingNotice?.booking_disabled} data-testid="sports-public-smart-search-btn"
                  style={{ width: '100%', background: bookingNotice?.booking_disabled ? '#2A2A2A' : T.gradient, color: '#fff', border: 'none', borderRadius: 8, padding: '13px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, cursor: (searching || bookingNotice?.booking_disabled) ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: bookingNotice?.booking_disabled ? 'none' : `0 0 20px ${T.glow}`, opacity: (searching || bookingNotice?.booking_disabled) ? 0.5 : 1 }}>
                  <Search style={{ width: 16, height: 16 }} /> {bookingNotice?.booking_disabled ? 'Bookings Unavailable' : (searching ? 'Searching…' : 'Check Availability')}
                </button>

                {/* ── Today's Slots quick view button ── */}
                {!smartResults && !searching && (
                  <button onClick={handleViewTodaySlots} data-testid="sports-public-today-slots-btn"
                    style={{ width: '100%', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px', fontSize: 12, fontWeight: 600, color: T.muted, cursor: 'pointer', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'all 0.2s', letterSpacing: 0.5 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.primary + '60'; e.currentTarget.style.color = T.text; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
                    <span style={{ fontSize: 14 }}>⊞</span>
                    {showTodaySlots ? 'Hide Today\'s Slots' : 'See Available Slots for Today'}
                  </button>
                )}

                {/* ── Today's Slots panel ── */}
                {showTodaySlots && todaySlots !== null && (
                  <div style={{ marginTop: 16 }} data-testid="sports-public-today-slots-panel">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, fontWeight: 700 }}>
                        Today's Open Slots
                      </p>
                      <p style={{ fontSize: 11, color: T.subtle }}>{new Date().toLocaleDateString('en-PH', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                    </div>

                    {loadingTodaySlots ? (
                      <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div className="animate-spin" style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${T.primary}`, borderTopColor: 'transparent', margin: '0 auto' }} />
                      </div>
                    ) : (
                      Object.entries(todaySlots).map(([courtId, courtData]) => {
                        const hasSlots = courtData.free_intervals?.length > 0;
                        return (
                          <div key={courtId} style={{ marginBottom: 14 }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                              {courtData.name}
                            </p>
                            {!courtData.open ? (
                              <p style={{ fontSize: 11, color: T.subtle, fontStyle: 'italic' }}>Closed today</p>
                            ) : !hasSlots ? (
                              <p style={{ fontSize: 11, color: '#FF2D55', fontWeight: 600 }}>Fully booked today</p>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {/* Available intervals — green/primary chips. Trim/hide anything already in the past. */}
                                {trimPastIntervals(courtData.free_intervals, todayLocal).map((iv, idx) => (
                                  <button key={idx}
                                    data-testid={`sports-today-slot-${courtId}-${idx}`}
                                    onClick={() => {
                                      const todayDate = todayLocal;
                                      setDate(todayDate);
                                      setStartTime(iv.start);
                                      setEndTime(iv.end);
                                      setCalOpen(false);
                                      setShowTodaySlots(false);
                                      setSmartResults(null);
                                      // Direct search with known values (bypass async state)
                                      setSearching(true); setError('');
                                      axios.post(`${BACKEND}/api/sports/public/${slug}/smart`, {
                                        date: todayDate, start_time: iv.start, end_time: iv.end,
                                        sport: selectedSport || sportFilter || undefined,
                                      }).then(r => setSmartResults(r.data))
                                        .catch(e => setError(errText(e, 'Search failed.')))
                                        .finally(() => setSearching(false));
                                    }}
                                    style={{ padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${T.primary}50`, background: T.primary + '15', color: T.primary, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = T.primary + '30'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = T.primary + '15'; }}>
                                    {fmt12(iv.start)} – {fmt12(iv.end)}
                                  </button>
                                ))}
                                {/* Taken visual indicator */}
                                {courtData.slots?.some(s => !s.available) && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: T.subtle, padding: '4px 8px' }}>
                                    <span style={{ width: 8, height: 8, borderRadius: 2, background: T.subtle + '40', display: 'inline-block', textDecoration: 'line-through' }} />
                                    some slots taken
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}

                    <p style={{ fontSize: 10, color: T.subtle, marginTop: 8, textAlign: 'center' }}>
                      Tap a time block to instantly check availability
                    </p>
                  </div>
                )}

                {/* Smart results — Layer 1 (full) + Layer 2 (partial) */}
                {smartResults && (
                  <div style={{ marginTop: 20 }} data-testid="sports-public-smart-results">

                    {/* Layer 1: Full coverage courts */}
                    {smartResults.courts?.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, marginBottom: 10, fontWeight: 700 }}>
                          Available Courts
                        </p>
                        {smartResults.courts.map((c, i) => (
                          <div key={c.court_id} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                              <p style={{ fontWeight: 700, color: T.text, fontSize: 14, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>{c.court_name}</p>
                              <p style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>{fmt12(c.start)} – {fmt12(c.end)}</p>
                            </div>
                            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 900, color: T.primary }}>₱{c.amount?.toLocaleString()}</p>
                              <button data-testid={`sports-public-book-court-${i}`}
                                onClick={() => { setSelectedOption({ court_id: c.court_id, date, start: c.start, end: c.end, amount: c.amount }); setStep('form'); }}
                                style={{ background: T.gradient, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
                                Book
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Layer 2: Partial coverage courts */}
                    {smartResults.partial_courts?.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: T.muted, marginBottom: 8, fontWeight: 700 }}>
                          {smartResults.courts?.length > 0 ? 'Also Available (Partial)' : 'Partially Available'}
                        </p>
                        {smartResults.partial_courts.map((p, i) => (
                          <div key={p.court_id + i} style={{ background: T.bg, border: `1px dashed ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                            {/* Court header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <p style={{ fontWeight: 700, color: T.muted, fontSize: 13, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>{p.court_name}</p>
                                <span style={{ fontSize: 9, background: T.muted + '22', color: T.muted, borderRadius: 4, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  {p.coverage_pct}% of your time
                                </span>
                              </div>
                              {p.coverage_note && <p style={{ fontSize: 10, color: T.subtle }}>{p.coverage_note}</p>}
                            </div>
                            {/* Individual bookable segments */}
                            {(p.segments || []).map((seg, si) => (
                              <div key={si} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', marginBottom: 6, borderRadius: 8, background: T.surface, border: `1px solid ${T.border}` }}>
                                <div>
                                  <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{fmt12(seg.start)} – {fmt12(seg.end)}</p>
                                  <p style={{ fontSize: 10, color: T.subtle }}>
                                    {Math.round((toMin(seg.end) - toMin(seg.start)) / 60 * 10) / 10}hr
                                  </p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 900, color: T.muted }}>₱{seg.amount?.toLocaleString()}</span>
                                  <button onClick={() => { setSelectedOption({ court_id: p.court_id, date, start: seg.start, end: seg.end, amount: seg.amount }); setStep('form'); }}
                                    style={{ background: T.muted + '20', color: T.muted, border: `1px solid ${T.muted}40`, borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif' }}>
                                    Book
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Courts free at this time but with a longer minimum booking —
                        shown grayed with a one-tap extension instead of vanishing
                        (owner 2026-08-17: silent exclusion reads as a broken search). */}
                    {smartResults.below_minimum?.length > 0 && (
                      <div style={{ marginBottom: 16 }} data-testid="sports-public-below-minimum">
                        <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: T.muted, marginBottom: 8, fontWeight: 700 }}>
                          Needs a longer booking
                        </p>
                        {smartResults.below_minimum.map((b, i) => {
                          const mins = b.min_minutes || 0;
                          const label = mins % 60 === 0 ? `${mins / 60} hour${mins > 60 ? 's' : ''}` : (mins > 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} minutes`);
                          return (
                            <div key={b.court_id + i} style={{ background: T.bg, border: `1px dashed ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                              <div>
                                <p style={{ fontWeight: 700, color: T.muted, fontSize: 13, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>{b.court_name}</p>
                                <p style={{ fontSize: 11, color: T.subtle, marginTop: 3 }}>⏱ Bookings here need at least {label}</p>
                              </div>
                              <button data-testid={`sports-public-below-min-extend-${i}`}
                                onClick={() => { setSelectedOption({ court_id: b.court_id, date, start: b.start, end: b.suggested_end, amount: b.amount }); setStep('form'); }}
                                style={{ background: `${T.primary}14`, border: `1px solid ${T.primary}55`, color: T.text, borderRadius: 999, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
                                Book {fmt12(b.start)} – {fmt12(b.suggested_end)} · ₱{b.amount?.toLocaleString()}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* No results at all */}
                    {!smartResults.courts?.length && !smartResults.partial_courts?.length && !smartResults.below_minimum?.length && (
                      <p style={{ fontSize: 13, color: '#FF8080', marginBottom: 12, fontWeight: 600 }}>No courts available for that time.</p>
                    )}

                    {/* Coming days with the same time window free — always shown
                        (owner 2026-08-16): time-constrained customers scan days,
                        not times, so each day lists ALL courts that fit. */}
                    {smartResults.next_dates?.length > 0 && (
                      <div data-testid="sports-public-next-dates">
                        <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 8 }}>
                          {smartResults.courts?.length > 0 ? 'Also available on coming days' : 'Next available dates:'}
                        </p>
                        {smartResults.next_dates.map((nd, i) => (
                          <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: nd.courts?.length ? 8 : 0 }}>
                              <p style={{ fontSize: 13, fontWeight: 700, color: T.primary }}>{fmtDateShort(nd.date)}</p>
                              <p style={{ fontSize: 12, color: T.muted }}>{fmt12(nd.start)} – {fmt12(nd.end)}</p>
                            </div>
                            {nd.courts?.length ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {nd.courts.map((c, ci) => (
                                  <button key={ci} data-testid={`sports-next-date-${i}-court-${ci}`}
                                    onClick={() => { setSelectedOption({ court_id: c.court_id, date: nd.date, start: nd.start, end: nd.end, amount: c.amount }); setStep('form'); }}
                                    style={{ padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${T.primary}50`, background: T.primary + '15', color: T.primary, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = T.primary + '30'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = T.primary + '15'; }}>
                                    {c.court_name} · ₱{c.amount?.toLocaleString()}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <button onClick={() => { setDate(nd.date); setStartTime(nd.start); setEndTime(nd.end); setSmartResults(null); setCalOpen(true); }}
                                style={{ background: T.primary + '20', color: T.primary, border: `1px solid ${T.primary}50`, borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontWeight: 700, textTransform: 'uppercase' }}>
                                Try
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Alternative suggestions from 409 */}
                {alternatives && (
                  <div style={{ marginTop: 16, padding: 14, background: '#FF2D5510', border: '1px solid #FF2D5540', borderRadius: 10 }}>
                    <p style={{ fontSize: 12, color: '#FF8080', marginBottom: 8 }}>Alternative options:</p>
                    {/* Best recovery first: your exact time TODAY on another court */}
                    {alternatives.same_day_courts?.map((sc, i) => (
                      <p key={`sc-${i}`} style={{ fontSize: 12, color: T.muted, marginTop: i ? 4 : 0 }}>
                        Today on {sc.court_name}: {fmt12(sc.start)} – {fmt12(sc.end)} · ₱{sc.amount?.toLocaleString()}
                        <button data-testid={`sports-alt-same-day-court-${i}`}
                          onClick={() => { setSelectedOption({ court_id: sc.court_id, date: sc.date, start: sc.start, end: sc.end, amount: sc.amount }); setAlternatives(null); setError(''); setStep('form'); }}
                          style={{ marginLeft: 8, background: 'none', border: 'none', color: T.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Book</button>
                      </p>
                    ))}
                    {alternatives.same_day_partial && (
                      <p style={{ fontSize: 12, color: T.muted, marginTop: alternatives.same_day_courts?.length ? 4 : 0 }}>
                        Today: {fmt12(alternatives.same_day_partial.start)} – {fmt12(alternatives.same_day_partial.end)}
                        <button onClick={() => { setStartTime(alternatives.same_day_partial.start); setEndTime(alternatives.same_day_partial.end); setAlternatives(null); }}
                          style={{ marginLeft: 8, background: 'none', border: 'none', color: T.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Use</button>
                      </p>
                    )}
                    {alternatives.next_dates?.map((nd, i) => (
                      <p key={i} style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                        {fmtDateShort(nd.date)}: {fmt12(nd.start)} – {fmt12(nd.end)}
                        {(nd.courts?.length ? nd.courts : [null]).map((c, ci) => c ? (
                          <button key={ci} data-testid={`sports-alt-next-date-${i}-court-${ci}`}
                            onClick={() => { setSelectedOption({ court_id: c.court_id, date: nd.date, start: nd.start, end: nd.end, amount: c.amount }); setAlternatives(null); setError(''); setStep('form'); }}
                            style={{ marginLeft: 8, background: 'none', border: 'none', color: T.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                            {c.court_name} ₱{c.amount?.toLocaleString()}
                          </button>
                        ) : (
                          <button key={ci} onClick={() => { setDate(nd.date); setStartTime(nd.start); setEndTime(nd.end); setAlternatives(null); }}
                            style={{ marginLeft: 8, background: 'none', border: 'none', color: T.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Use</button>
                        ))}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Schedule tab ── */}
            {(step === 'idle' || step === 'results') && tab === 'schedule' && (
              <div data-testid="sports-public-schedule-tab">

                {/* Date navigation — arrows + label + calendar popup */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <button onClick={() => changeScheduleDate(-1)} data-testid="sports-schedule-prev-day"
                    style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.muted, borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', lineHeight: 1, flexShrink: 0 }}>
                    <ChevronLeft style={{ width: 16, height: 16 }} />
                  </button>

                  <div style={{ position: 'relative', flex: 1 }} onClick={e => e.stopPropagation()}>
                    <button
                      data-testid="sports-schedule-date-picker"
                      onClick={() => setScheduleCalOpen(c => !c)}
                      style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', outline: 'none' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: scheduleDate === today() ? T.primary : T.subtle, flexShrink: 0 }} />
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, lineHeight: 1.2 }}>
                          {fmtDateChip(scheduleDate)}
                        </p>
                        {scheduleDate === today() && <span style={{ fontSize: 9, color: T.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Today</span>}
                      </div>
                      <ChevronDown style={{ width: 14, height: 14, color: T.subtle, marginLeft: 'auto' }} />
                    </button>
                    {scheduleCalOpen && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 4 }}>
                        <BookingCalendar
                          value={scheduleDate}
                          onChange={d => { setScheduleDate(d); setScheduleCalOpen(false); }}
                          slug={slug}
                          T={T}
                          maxDate={maxDate}
                          minDate={todayLocal}
                        />
                      </div>
                    )}
                  </div>

                  <button onClick={() => changeScheduleDate(1)} data-testid="sports-schedule-next-day"
                    style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.muted, borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', lineHeight: 1, flexShrink: 0 }}>
                    <ChevronRight style={{ width: 16, height: 16 }} />
                  </button>

                  {scheduleDate !== today() && (
                    <button onClick={() => setScheduleDate(today())} data-testid="sports-schedule-today-btn"
                      style={{ background: T.primary + '18', border: `1px solid ${T.primary}40`, color: T.primary, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
                      Today
                    </button>
                  )}
                </div>

                {/* Quick day pills */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[0, 1, 2, 3].map(offset => {
                    const d = new Date(); d.setDate(d.getDate() + offset);
                    const ds = ymd(d);
                    const isSelected = ds === scheduleDate;
                    const label = offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow'
                      : `${DOW_SHORT[d.getDay()]} ${d.getDate()}`;
                    return (
                      <button key={ds} onClick={() => setScheduleDate(ds)}
                        style={{ padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', letterSpacing: 0.5, textTransform: 'uppercase',
                          border: `1.5px solid ${isSelected ? T.primary : T.border}`,
                          background: isSelected ? T.primary + '20' : 'transparent',
                          color: isSelected ? T.primary : T.subtle }}>
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Grid */}
                {scheduleLoading ? (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <div className="animate-spin" style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${T.primary}`, borderTopColor: 'transparent', margin: '0 auto 8px' }} />
                    <p style={{ fontSize: 12, color: T.muted }}>Loading schedule…</p>
                  </div>
                ) : (
                  <>
                  <p style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
                    <span style={{ color: T.primary, fontWeight: 700 }}>+</span> Tap any open slot to book it.
                  </p>
                  <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${T.border}` }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: `${40 + filteredCourts.length * 120}px` }}>
                      <thead>
                        <tr>
                          <th style={{ width: 40, background: T.surface, borderBottom: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}`, padding: '8px 4px', position: 'sticky', left: 0, zIndex: 4 }}>
                            <span style={{ fontSize: 8, color: T.subtle, textTransform: 'uppercase', letterSpacing: 1 }}>Time</span>
                          </th>
                          {filteredCourts.map(c => (
                            <th key={c.id} style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}`, padding: '8px 10px', textAlign: 'center' }}>
                              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5, color: T.text }}>{c.name}</p>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {scheduleTimeMarks.map((timeMin, rowIdx) => {
                          const isHour = timeMin % 60 === 0;
                          const bTop = isHour && rowIdx > 0 ? `1px solid ${T.border}` : `1px solid ${T.bg}`;
                          return (
                            <tr key={timeMin}>
                              {/* Time label — sticky */}
                              <td style={{ width: 40, background: T.surface, borderRight: `1px solid ${T.border}`, borderTop: bTop, position: 'sticky', left: 0, zIndex: 2, padding: isHour ? '4px 4px 0' : '2px 4px 0', verticalAlign: 'top', height: isHour ? 44 : 28 }}>
                                {isHour && <span style={{ fontSize: 10, fontWeight: 700, color: T.subtle, fontFamily: 'monospace' }}>{fmtTimeMark(timeMin)}</span>}
                              </td>
                              {filteredCourts.map(court => {
                                const key = `${court.id}|${timeMin}`;
                                const cell = scheduleGrid[key];
                                if (!cell || cell.type === 'skip') return null;

                                if (cell.type === 'booked') return (
                                  <td key={court.id} rowSpan={cell.rowSpan}
                                    style={{ background: T.primary + '18', borderLeft: `2px solid ${T.primary + '35'}`, borderTop: bTop, borderRight: `1px solid ${T.border}`, verticalAlign: 'middle', padding: '4px 6px', textAlign: 'center' }}>
                                    <p style={{ fontSize: 9, color: T.primary + '70', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Booked</p>
                                  </td>
                                );

                                if (cell.type === 'available') {
                                  const slot = cell.slot;
                                  const past = slotIsPast(slot, scheduleDate);
                                  if (past) {
                                    return (
                                      <td key={court.id} rowSpan={cell.rowSpan}
                                        title="This time has already passed"
                                        style={{ background: T.surface, borderTop: bTop, borderRight: `1px solid ${T.border}`, opacity: 0.32, verticalAlign: 'middle', height: isHour ? 44 : 28, textAlign: 'center' }}>
                                        <p style={{ fontSize: 8, color: T.subtle, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, textDecoration: 'line-through' }}>Past</p>
                                      </td>
                                    );
                                  }
                                  return (
                                    <td key={court.id} rowSpan={cell.rowSpan}
                                      data-testid={`sports-schedule-slot-${court.id}-${timeMin}`}
                                      onClick={() => {
                                        // Divert to Browse Courts (owner 2026-08-17): land on THIS
                                        // court + date with the tapped slot pre-selected as the start,
                                        // so the customer picks their own end time with the full grid
                                        // and its min-duration hints — instead of a fixed 30-min
                                        // smart search that can bounce off booking minimums.
                                        setTab('courts');
                                        setSelectedCourtId(court.id);
                                        setCourtDate(scheduleDate);
                                        setPendingSlotStart(slot.start);
                                        setSmartResults(null); setError('');
                                        setStep('idle');
                                      }}
                                      style={{ background: T.bg, borderTop: bTop, borderRight: `1px solid ${T.border}`, cursor: 'pointer', transition: 'background 0.1s', verticalAlign: 'middle', height: isHour ? 44 : 28, textAlign: 'center' }}
                                      onMouseEnter={e => e.currentTarget.style.background = T.primary + '12'}
                                      onMouseLeave={e => e.currentTarget.style.background = T.bg}
                                    >
                                      {/* Quiet affordance: open cells advertise they're bookable */}
                                      <span style={{ fontSize: 11, fontWeight: 700, color: T.primary, opacity: 0.4, lineHeight: 1, pointerEvents: 'none' }}>+</span>
                                    </td>
                                  );
                                }

                                return (
                                  <td key={court.id} style={{ background: T.surface, borderTop: bTop, borderRight: `1px solid ${T.border}`, opacity: 0.4 }} />
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}

                {/* Legend */}
                <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: T.subtle }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: T.primary + '18', borderLeft: `2px solid ${T.primary + '35'}`, display: 'inline-block' }} />
                    Booked
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: T.subtle }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: T.bg, border: `1px solid ${T.border}`, display: 'inline-block' }} />
                    Available — tap to book
                  </div>
                </div>
              </div>
            )}

            {/* ── Browse Courts tab ── */}
            {(step === 'idle' || step === 'results') && tab === 'courts' && (
              <div data-testid="sports-public-courts-tab">
                <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, textTransform: 'uppercase', color: T.text, marginBottom: 16 }}>Browse Courts</h2>

                {/* Court selector */}
                {!selectedCourtId ? (
                  <div>
                    <p style={{ fontSize: 13, color: T.muted, marginBottom: 12 }}>Select a court to view availability:</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {filteredCourts.map(c => (
                        <button key={c.id} data-testid={`sports-public-select-court-${c.id}`}
                          onClick={() => setSelectedCourtId(c.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = T.primary + '60'; e.currentTarget.style.background = T.primary + '08'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bg; }}>
                          <span style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.surface, borderRadius: 8, flexShrink: 0 }}>
                            <SportIcon sport={c.sport || 'other'} size={26} color={T.primary} />
                          </span>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, textTransform: 'uppercase', color: T.text }}>{c.name}</p>
                            <p style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{c.sport.charAt(0).toUpperCase()+c.sport.slice(1)} · {hoursLabel(c)}</p>
                            {/* Today's free time at a glance — tap the card to book any of it */}
                            {(() => {
                              if (!cardsToday) return <p style={{ fontSize: 11, color: T.subtle, marginTop: 4 }}>Checking availability…</p>;
                              const td = cardsToday[c.id];
                              if (!td) return null;
                              if (!td.open) return <p style={{ fontSize: 11, color: T.subtle, fontStyle: 'italic', marginTop: 4 }}>Closed today</p>;
                              const ivs = trimPastIntervals(td.free_intervals, todayLocal);
                              if (!ivs.length) return <p style={{ fontSize: 11, color: '#FF2D55', fontWeight: 600, marginTop: 4 }}>Fully booked today</p>;
                              return (
                                <p style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }} data-testid={`sports-court-card-today-${c.id}`}>
                                  <span style={{ color: T.subtle }}>Available today: </span>
                                  <span style={{ color: T.primary, fontWeight: 600 }}>
                                    {ivs.slice(0, 3).map(iv => `${fmt12(iv.start)}–${fmt12(iv.end)}`).join(' · ')}
                                  </span>
                                  {ivs.length > 3 && <span style={{ color: T.subtle }}> +{ivs.length - 3} more</span>}
                                </p>
                              );
                            })()}
                          </div>
                          <ChevronDown style={{ width: 16, height: 16, color: T.subtle, transform: 'rotate(-90deg)' }} />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Court header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                      <button onClick={() => { setSelectedCourtId(''); setSlotData(null); setRangeStart(null); setRangeEnd(null); }}
                        style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>
                        <X style={{ width: 16, height: 16 }} />
                      </button>
                      <div>
                        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, textTransform: 'uppercase', color: T.text }}>{selectedCourt?.name}</p>
                        <UrgencyTicker urgency={urgency} T={T} />
                      </div>
                    </div>

                    {/* Calendar date picker for Browse Courts (per-court availability) */}
                    <div style={{ marginBottom: 14 }}>
                      <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 6 }}>Select Date</span>
                      <BookingCalendar
                        value={courtDate}
                        onChange={d => { setCourtDate(d); setRangeStart(null); setRangeEnd(null); setPlayNowSel(false); setPnAmount(null); }}
                        slug={slug}
                        T={T}
                        courtId={selectedCourtId}
                        maxDate={maxDate}
                        minDate={todayLocal}
                        data-testid="sports-public-court-date"
                      />
                    </div>

                    {slotLoading && <div style={{ textAlign: 'center', padding: 24, color: T.muted, fontSize: 13 }}>Loading slots…</div>}

                    {/* Availability fetch failed: say so + retry — a silently
                        empty grid reads as "closed", which is anti-revenue. */}
                    {!slotLoading && !slotData && slotError && (
                      <div style={{ textAlign: 'center', padding: 24 }}>
                        <div style={{ color: T.muted, fontSize: 13, marginBottom: 12 }}>
                          Couldn't load the schedule — check your connection.
                        </div>
                        <button
                          onClick={() => loadCourtSlots()}
                          style={{
                            padding: '9px 22px', borderRadius: 999, cursor: 'pointer',
                            background: T.primary + '14', border: `1px solid ${T.primary}55`,
                            color: T.text, fontSize: 13, fontWeight: 600,
                          }}
                        >
                          Try again
                        </button>
                      </div>
                    )}

                    {!slotLoading && slotData && (
                      <div>
                        {!slotData.open ? (
                          <p style={{ fontSize: 13, color: T.muted, textAlign: 'center', padding: 24 }}>Court is closed on this date.</p>
                        ) : (
                          <>
                            {slotData.play_now?.available && (
                              <button
                                data-testid="sports-public-play-now-btn"
                                onClick={() => {
                                  const si = pnIdx(slotData);
                                  setPlayNowSel(true); setRangeStart(si); setRangeEnd(si + 1); setError('');
                                }}
                                style={{
                                  width: '100%', marginBottom: 14, padding: '14px 16px', borderRadius: 12,
                                  border: playNowSel ? '2px solid #00E676' : '1.5px solid #00E67655',
                                  background: playNowSel ? '#00E67622' : 'linear-gradient(90deg,#00E67614,#00E6760a)',
                                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, textAlign: 'left',
                                }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <Zap style={{ width: 20, height: 20, color: '#00E676' }} />
                                  <div>
                                    <p style={{ fontSize: 13, fontWeight: 800, color: '#00E676', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                      Play Now — Court is free {playNowSel && '· Selected'}
                                    </p>
                                    <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                                      {playNowSel
                                        ? 'Tap a later slot below to play longer (extra hours at standard rate)'
                                        : `Start ${fmt12(slotData.play_now.start)} · ${slotData.play_now.minutes} min play · pro-rated`}
                                    </p>
                                  </div>
                                </div>
                                <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 900, color: '#00E676' }}>
                                  ₱{Number((playNowSel && pnAmount != null ? pnAmount : slotData.play_now.price) || 0).toLocaleString()}
                                </span>
                              </button>
                            )}
                            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: T.subtle, marginBottom: 2 }}>Tap your start time, then tap your end time</p>
                            <p style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>
                              Your booking can cross time groups — 11:00 AM to 2:00 PM works with one tap each.
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                              {slotData.slots?.map((slot, i) => {
                                const hasRange = rangeStart !== null && rangeEnd !== null;
                                // Solid fill ONLY for slots actually inside the booking [start, end).
                                const inFill = hasRange && i >= rangeStart && i < rangeEnd;
                                const isStart = i === rangeStart;
                                // The end tap is a BOUNDARY ("booking stops at this cell's start").
                                // Paint it as a dashed outline, never solid — a booked slot used as
                                // a boundary must not look like it was selected into the booking.
                                const isEndBoundary = hasRange && i === rangeEnd;
                                const isSelected = isStart || inFill;
                                const past = slotIsPast(slot, courtDate);
                                // Treat past slots as unbookable regardless of backend availability flag.
                                const bookable = !past && slot.available;
                                // While picking an END (start chosen, end not yet): the first taken
                                // slot after the start's free run is a LEGAL end tap — the booking
                                // stops where that one begins. Light it up so it doesn't read as blocked.
                                const boundaryHint = !slot.available && !past && !playNowSel
                                  && rangeStart !== null && rangeEnd === null && i > rangeStart
                                  && slotData.slots.slice(rangeStart, i).every(s => s.available && !slotIsPast(s, courtDate));
                                const dim = !bookable && !isSelected && !isEndBoundary && !boundaryHint;
                                // Band header before the first chip of each day part (visual only).
                                const band = timeBandIdx(slot.start);
                                const newBand = i === 0 || timeBandIdx(slotData.slots[i - 1].start) !== band;
                                const bandSub = (() => {
                                  if (!newBand) return '';
                                  if (band === 3) {
                                    try {
                                      const d = new Date(`${courtDate}T00:00:00`);
                                      d.setDate(d.getDate() + 1);
                                      // Local date parts, NOT toISOString() — that shifts to UTC
                                      // and rolls the day back for +08 facilities.
                                      const p = (n) => String(n).padStart(2, '0');
                                      return `${fmtDateShort(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`)} · same session`;
                                    } catch { return 'same session'; }
                                  }
                                  let last = i;
                                  while (last + 1 < slotData.slots.length && timeBandIdx(slotData.slots[last + 1].start) === band) last++;
                                  return last > i ? `${fmt12(slot.start)} – ${fmt12(slotData.slots[last].start)}` : fmt12(slot.start);
                                })();
                                const BandIcon = TIME_BANDS[band].Icon;
                                return (
                                  <Fragment key={i}>
                                  {newBand && (
                                    <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, marginTop: i === 0 ? 0 : 10 }}>
                                      <BandIcon size={14} style={{ color: T.primary, flexShrink: 0 }} />
                                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: T.text, fontFamily: 'Oswald, sans-serif' }}>{TIME_BANDS[band].label}</span>
                                      <span style={{ fontSize: 11, color: T.subtle }}>{bandSub}</span>
                                      <span style={{ flex: 1, borderTop: `1px solid ${T.border}` }} />
                                    </div>
                                  )}
                                  <button data-testid={`sports-public-slot-${i}`}
                                    disabled={past || (!slot.available && rangeStart === null && !playNowSel)}
                                    title={past ? 'This time has already passed'
                                      : boundaryHint ? `You can end here — the next booking starts at ${fmt12(slot.start)}` : undefined}
                                    onClick={() => {
                                      if (past) return;
                                      if (playNowSel) {
                                        const si = pnIdx(slotData);
                                        if (i <= si) { // tapped at/before the in-progress slot → exit Play Now, start a normal selection
                                          setPlayNowSel(false);
                                          if (bookable) { setRangeStart(i); setRangeEnd(null); }
                                          setError(''); return;
                                        }
                                        if (slotData.slots.slice(si, i).every(s => s.available && !slotIsPast(s, courtDate))) { setRangeEnd(i); setError(''); }
                                        else setError('Some slots before that time are taken. Pick an earlier end.');
                                        return;
                                      }
                                      // Strict cycle: 1st tap = start · 2nd tap = end · 3rd tap = cancel (then restart)
                                      if (rangeStart !== null && rangeEnd !== null) { setRangeStart(null); setRangeEnd(null); setError(''); return; }
                                      if (rangeStart === null) { if (!bookable) return; setRangeStart(i); setRangeEnd(null); setError(''); return; }
                                      if (i === rangeStart) { setRangeStart(null); setRangeEnd(null); setError(''); return; }
                                      if (i < rangeStart) { if (bookable) { setRangeStart(i); setError(''); } return; }
                                      if (slotData.slots.slice(rangeStart, i).every(s => s.available && !slotIsPast(s, courtDate))) { setRangeEnd(i); setError(''); }
                                      else setError('Some slots in that range are taken. Select a different end time.');
                                    }}
                                    style={{
                                      background: isEndBoundary ? `${T.primary}22` : boundaryHint ? `${T.primary}10` : dim ? T.surface : isSelected ? T.primary : T.bg,
                                      color: isEndBoundary || boundaryHint ? T.text : dim ? T.subtle : isSelected ? '#fff' : T.muted,
                                      border: `1px ${(isEndBoundary || boundaryHint) ? 'dashed' : 'solid'} ${(isEndBoundary || boundaryHint) ? T.primary : dim ? T.border : isSelected ? T.primary : T.border}`,
                                      borderRadius: 6, padding: '6px 10px', fontSize: 11, cursor: past ? 'not-allowed' : dim ? 'not-allowed' : 'pointer',
                                      fontFamily: 'Manrope, sans-serif', fontWeight: (isSelected || isEndBoundary || boundaryHint) ? 700 : 400,
                                      opacity: dim ? (past ? 0.28 : 0.4) : 1, transition: 'all 0.15s',
                                      textDecoration: dim ? 'line-through' : 'none',
                                    }}>
                                    {fmt12(slot.start)}
                                  </button>
                                  </Fragment>
                                );
                              })}
                              {slotData.slots?.length > 0 && (() => {
                                const N = slotData.slots.length;
                                const isEnd = rangeEnd === N;
                                const disabled = rangeStart === null && !playNowSel;
                                return (
                                  <button key="end-cap" data-testid="sports-public-slot-end"
                                    title="End of day (closing time)"
                                    disabled={disabled}
                                    onClick={() => {
                                      if (rangeEnd !== null && !playNowSel) { setRangeStart(null); setRangeEnd(null); setError(''); return; }
                                      const start = playNowSel ? pnIdx(slotData) : rangeStart;
                                      if (start == null || start < 0) return;
                                      if (slotData.slots.slice(start, N).every(s => s.available)) { setRangeEnd(N); setError(''); }
                                      else setError('Some slots in that range are taken. Select a different end time.');
                                    }}
                                    style={{
                                      background: isEnd ? T.primary : T.bg,
                                      color: isEnd ? '#fff' : T.muted,
                                      border: `1px dashed ${isEnd ? T.primary : T.border}`,
                                      borderRadius: 6, padding: '6px 10px', fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer',
                                      fontFamily: 'Manrope, sans-serif', fontWeight: isEnd ? 700 : 400,
                                      opacity: disabled ? 0.4 : 1, transition: 'all 0.15s',
                                    }}>
                                    {fmt12(slotData.slots[N - 1].end)}
                                  </button>
                                );
                              })()}
                            </div>

                            {error && <p style={{ color: '#FF2D55', fontSize: 12, marginBottom: 10 }}>{error}</p>}

                            {!playNowSel && rangeStart !== null && rangeEnd === null && (
                              <p data-testid="sports-public-tap-end-hint" style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>Now tap your end time</p>
                            )}

                            {rangeStart !== null && rangeEnd !== null && (
                              <div style={{ background: T.bg, border: `1px solid ${playNowSel ? '#00E676' : T.primary}40`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div>
                                    <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                                      {fmt12(playNowSel ? slotData.play_now.start : slotData.slots[rangeStart]?.start)} – {fmt12(pointTime(slotData, rangeEnd))}
                                    </p>
                                    <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }} data-testid="sports-public-range-summary">
                                      {playNowSel
                                        ? <>Play Now · pro-rated start{pnAmount != null && <> · <strong style={{ color: '#00E676' }}>₱{Number(pnAmount).toLocaleString()}</strong></>}</>
                                        : <>{`${((t2m(pointTime(slotData, rangeEnd)) - t2m(slotData.slots[rangeStart]?.start)) / 60).toFixed(1)} hours`}{rangeAmount != null && <> · <strong style={{ color: '#00E676' }} data-testid="sports-public-range-price">₱{Number(rangeAmount).toLocaleString()}</strong></>}</>}
                                    </p>
                                  </div>
                                  <button data-testid="sports-public-court-book-btn"
                                    onClick={() => setStep('form')}
                                    disabled={!playNowSel && !!rangeError}
                                    style={{ background: (!playNowSel && rangeError) ? '#2A2A2A' : T.gradient, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', cursor: (!playNowSel && rangeError) ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, opacity: (!playNowSel && rangeError) ? 0.6 : 1 }}>
                                    Book
                                  </button>
                                </div>
                                {!playNowSel && rangeError && (() => {
                                  // Suggest the earliest valid end for the chosen start (tappable).
                                  const startT = slotData.slots[rangeStart]?.start;
                                  const smv = slotData.court?.slot_minutes || 30;
                                  const effC = effectiveCourt(courts.find(c => c.id === selectedCourtId) || {}, courtDate);
                                  let minNeed = startT ? minDurationAt(effC, toMin(startT)) : 0;
                                  // Squeeze-aware: cap at the free run from the start slot.
                                  let windowMin = 0; let wi = rangeStart;
                                  while (wi < slotData.slots.length && slotData.slots[wi]?.available) { windowMin += smv; wi++; }
                                  if (windowMin > 0 && windowMin < minNeed) minNeed = windowMin;
                                  const endIdx = rangeStart + Math.ceil(minNeed / smv);
                                  const suggEnd = (minNeed > 0 && windowMin >= minNeed) ? pointTime(slotData, Math.min(endIdx, slotData.slots.length)) : null;
                                  return (
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                                      <p data-testid="sports-public-range-error" style={{ fontSize: 12, color: '#FF2D55' }}>
                                        ⏱ {rangeError}
                                      </p>
                                      {suggEnd && (
                                        <button data-testid="sports-public-range-suggest"
                                          onClick={() => setRangeEnd(Math.min(endIdx, slotData.slots.length))}
                                          style={{
                                            marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                                            background: `${T.primary}18`, border: `1px solid ${T.primary}50`,
                                            color: T.text, borderRadius: 8, padding: '7px 14px',
                                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                            fontFamily: 'Manrope, sans-serif',
                                          }}>
                                          <span style={{ color: T.primary }}>↳</span>
                                          Book {fmt12(startT)} – {fmt12(suggEnd)} instead
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Sibling courts' free time for this date — reacts to the
                        date picker; [View] hops courts keeping the same day. */}
                    {otherCourtsDay?.date === courtDate && otherCourtsDay.courts.length > 0 && (
                      <div style={{ marginTop: 22 }} data-testid="sports-public-other-courts">
                        <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: T.subtle, fontWeight: 700, marginBottom: 10 }}>
                          Also available on {fmtDateShort(courtDate)}
                        </p>
                        {otherCourtsDay.courts.map(oc => {
                          const ivs = oc.open ? trimPastIntervals(oc.free_intervals, courtDate) : [];
                          return (
                            <div key={oc.courtId} style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', color: T.text }}>{oc.name}</p>
                                {!oc.open ? (
                                  <p style={{ fontSize: 11, color: T.subtle, fontStyle: 'italic', marginTop: 2 }}>Closed this day</p>
                                ) : !ivs.length ? (
                                  <p style={{ fontSize: 11, color: '#FF2D55', fontWeight: 600, marginTop: 2 }}>Fully booked</p>
                                ) : (
                                  <p style={{ fontSize: 11.5, color: T.primary, fontWeight: 600, marginTop: 2, lineHeight: 1.5 }}>
                                    {ivs.slice(0, 3).map(iv => `${fmt12(iv.start)}–${fmt12(iv.end)}`).join(' · ')}
                                    {ivs.length > 3 && <span style={{ color: T.subtle, fontWeight: 400 }}> +{ivs.length - 3} more</span>}
                                  </p>
                                )}
                              </div>
                              {oc.open && ivs.length > 0 && (
                                <button data-testid={`sports-other-court-view-${oc.courtId}`}
                                  onClick={() => { setSelectedCourtId(oc.courtId); setSlotData(null); setRangeStart(null); setRangeEnd(null); setPlayNowSel(false); setPnAmount(null); }}
                                  style={{ background: `${T.primary}14`, border: `1px solid ${T.primary}55`, color: T.text, borderRadius: 999, padding: '7px 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', flexShrink: 0 }}>
                                  View
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </section>
      )}
      </>
      )}

      {/* ═══ Facility info — home page only: amenities, getting there, house rules ═══ */}
      {activePage === 'home' && ((siteBuilder.amenities || []).length > 0 || facility?.location || siteBuilder.directions || siteBuilder.house_rules) && (
        <section data-testid="public-info-section" style={{ maxWidth: 900, margin: '0 auto', padding: '8px 20px 40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {(siteBuilder.amenities || []).length > 0 && (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 18px' }}>
                <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: T.text, marginBottom: 12 }}>Amenities</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {siteBuilder.amenities.filter(id => AMENITIES[id]).map(id => {
                    const { label, Icon } = AMENITIES[id];
                    return (
                      <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, borderRadius: 999, padding: '5px 12px', fontSize: 11.5, color: T.text }}>
                        <Icon style={{ width: 13, height: 13, color: T.primary }} /> {label}
                      </span>
                    );
                  })}
                </div>
                {amenitiesPhotos.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }} data-testid="public-amenities-photos">
                    {amenitiesPhotos.map((g, i) => (
                      <img key={i} src={g.thumb_url || g.url} alt={g.caption || ''} loading="lazy"
                        onClick={() => setLightbox({ list: amenitiesPhotos, idx: i })}
                        style={{ width: 76, height: 56, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: `1px solid ${T.border}` }} />
                    ))}
                  </div>
                )}
              </div>
            )}
            {(facility?.location || siteBuilder.directions) && (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 18px' }}>
                <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: T.text, marginBottom: 10 }}>Getting there</h3>
                {siteBuilder.directions && (
                  <p style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.7, marginBottom: 8, whiteSpace: 'pre-line' }}>{siteBuilder.directions}</p>
                )}
                {facility?.location && (
                  <p style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.6, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <MapPin style={{ width: 13, height: 13, color: T.primary, flexShrink: 0, marginTop: 2 }} /> {facility.location}
                  </p>
                )}
                {facility?.location && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <a href={facility.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(facility.location)}`}
                      target="_blank" rel="noreferrer" data-testid="public-maps-btn"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 999, padding: '7px 14px', fontSize: 11.5, fontWeight: 700, color: T.text, textDecoration: 'none' }}>
                      <MapPin style={{ width: 12, height: 12, color: T.primary }} /> Google Maps
                    </a>
                    <a href={`https://waze.com/ul?q=${encodeURIComponent(facility.location)}`}
                      target="_blank" rel="noreferrer" data-testid="public-waze-btn"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 999, padding: '7px 14px', fontSize: 11.5, fontWeight: 700, color: T.text, textDecoration: 'none' }}>
                      <Navigation style={{ width: 12, height: 12, color: T.primary }} /> Waze
                    </a>
                  </div>
                )}
              </div>
            )}
            {siteBuilder.house_rules && (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 18px' }}>
                <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: T.text, marginBottom: 10 }}>House rules</h3>
                <p style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{siteBuilder.house_rules}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══ Gallery lightbox — full-res image + caption, arrow navigation ═══ */}
      {lightbox && lightbox.list[lightbox.idx] && (
        <div onClick={() => setLightbox(null)} data-testid="public-lightbox"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <style>{`@keyframes agLbIn { from { opacity: 0; transform: scale(0.97) translateX(16px); } to { opacity: 1; transform: none; } }`}</style>
          <button onClick={() => setLightbox(null)} aria-label="Close" data-testid="public-lightbox-close"
            style={{ position: 'absolute', top: 18, right: 22, background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 40, height: 40, borderRadius: '50%', fontSize: 18, cursor: 'pointer' }}>
            ✕
          </button>
          {lightbox.list.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setLightbox({ ...lightbox, idx: (lightbox.idx - 1 + lightbox.list.length) % lightbox.list.length }); }}
              aria-label="Previous photo" data-testid="public-lightbox-prev"
              style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 44, height: 44, borderRadius: '50%', fontSize: 22, cursor: 'pointer', zIndex: 2 }}>
              ‹
            </button>
          )}
          <figure key={lightbox.idx} onClick={e => e.stopPropagation()}
            style={{ margin: 0, textAlign: 'center', maxWidth: '88vw', animation: 'agLbIn 0.35s ease' }}>
            <ProgressiveLightboxImg
              thumb={lightbox.list[lightbox.idx].thumb_url || lightbox.list[lightbox.idx].url}
              full={lightbox.list[lightbox.idx].url}
              alt={lightbox.list[lightbox.idx].caption || ''}
              style={{ maxWidth: '88vw', maxHeight: '76vh', borderRadius: 12, boxShadow: `0 0 90px ${T.glow}`, display: 'block', margin: '0 auto' }} />
            <figcaption style={{ color: '#fff', marginTop: 14, fontSize: 14, fontFamily: 'Manrope, sans-serif' }}>
              {lightbox.list[lightbox.idx].caption || ''}
              <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: 10, fontSize: 12 }}>{lightbox.idx + 1} / {lightbox.list.length}</span>
            </figcaption>
          </figure>
          {lightbox.list.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setLightbox({ ...lightbox, idx: (lightbox.idx + 1) % lightbox.list.length }); }}
              aria-label="Next photo" data-testid="public-lightbox-next"
              style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 44, height: 44, borderRadius: '50%', fontSize: 22, cursor: 'pointer', zIndex: 2 }}>
              ›
            </button>
          )}
        </div>
      )}

      {/* ═══ Footer ═══ */}
      <footer style={{ borderTop: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 11, color: T.subtle }}>
            Powered by <a href="/" style={{ color: T.primary, textDecoration: 'none', fontWeight: 700 }}>AgriBooks Sports</a>
          </p>
          <span style={{ flex: 1 }} />
          {siteBuilder.social_links?.facebook && (
            <a href={siteBuilder.social_links.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" style={{ color: T.muted }}><Facebook style={{ width: 16, height: 16 }} /></a>
          )}
          {siteBuilder.social_links?.instagram && (
            <a href={siteBuilder.social_links.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" style={{ color: T.muted }}><Instagram style={{ width: 16, height: 16 }} /></a>
          )}
          {siteBuilder.social_links?.youtube && (
            <a href={siteBuilder.social_links.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" style={{ color: T.muted }}><Youtube style={{ width: 16, height: 16 }} /></a>
          )}
          {siteBuilder.social_links?.tiktok && (
            <a href={siteBuilder.social_links.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" style={{ color: T.muted }}><Globe style={{ width: 16, height: 16 }} /></a>
          )}
          {siteBuilder.social_links?.website && (
            <a href={siteBuilder.social_links.website} target="_blank" rel="noreferrer" aria-label="Website" style={{ color: T.muted }}><ExternalLink style={{ width: 16, height: 16 }} /></a>
          )}
        </div>
      </footer>
    </div>
  );
}
